import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

// The original animation ran on a fixed 50ms setInterval, so every per-tick
// increment below (speed, wingPhase, blinkPhase, walkPhase) is expressed in
// "per 50ms" units. We now drive the same maths from requestAnimationFrame and
// scale each increment by (elapsed / TICK_MS), which reproduces identical motion
// per unit of wall-clock time while rendering at the display refresh rate.
const TICK_MS = 50;

// Deterministic pseudo-noise in roughly [-1, 1], for creatures that should look
// like they are making it up as they go.
//
// It has to be a pure function of age rather than a per-frame random, or the
// motion would depend on frame rate and the delta-scaling guarantee below would
// no longer hold. Three incommensurate frequencies, so the pattern never
// visibly repeats even though it is entirely reproducible.
const wander = (age, seed, freq) =>
  Math.sin(age * freq + seed * 1.7) * 0.55 +
  Math.sin(age * freq * 2.3 + seed * 3.1) * 0.30 +
  Math.sin(age * freq * 5.7 + seed * 5.3) * 0.15;

// The chase: two figures in 100x100 boxes, both facing LEFT like every other
// sprite so the usual scaleX(-1) mirrors them. Drawn as thick round-capped
// strokes - at ~100px a stroke figure reads as a cut-paper silhouette, which
// is the poster look this theme trades on. Two frames each; the loop flips
// between them by age, so there is no sprite sheet and no re-render.
//
// Each frame: [head cx, head cy, head r, ...stroke paths].
const TEEN_FRAMES = [
  // Stride open: back leg trailing, front leg reaching, arms pumping opposite.
  { head: [38, 13, 7], paths: ['M42,21 L52,52', 'M47,31 L30,40', 'M47,31 L62,22', 'M52,52 L30,68 L20,88', 'M52,52 L66,66 L80,78'] },
  // Legs crossing under the body, arms swapped.
  { head: [40, 14, 7], paths: ['M43,22 L51,53', 'M47,32 L60,42', 'M47,32 L34,22', 'M51,53 L46,74 L38,92', 'M51,53 L58,74 L66,92'] }
];

// The machete is not a stroke. Drawn with the limb stroke it was just a
// longer arm; it is a FILLED shape with a glint along the spine so it
// separates from the black of the figure. And it is not a knife: longer,
// broader toward the tip, a flared belly on the cutting edge and a clipped
// end instead of a needle point.
//
// Held AHEAD and angled UP. The figure faces left, so forward is left: the
// arm reaches from the shoulder (50,30) to a hand at (28,34), and the blade
// climbs forward-and-up from there at about 40 degrees to a tip around
// (7,16). Spine on the upper-right side, cutting edge on the lower-left.
const MACHETE = {
  // spine grip->tip, clipped tip, then the belly back to the grip
  blade: 'M30,32 L18,21 L13,15 L7.5,15.5 L7,22 L14,26.5 L26.5,36 Z',
  guard: 'M25.5,37 L30.5,31',      // short, perpendicular to the blade
  glint: 'M12.5,15.8 L29,31.5'     // the lit spine, tip to grip
};

// The mask: a white hockey shell. A rounded crown tapering to a chin, two
// round eye holes lit red, and a scatter of dark vent holes across the lower
// face. Off-white rather than pure, so it does not blow out at scare size.
// The same geometry serves the small mask on the chase figure and the big
// one in the jump scare; only the viewBox differs.
//
// The eyes are circles drawn as paths - two equal arcs - rather than
// <circle> elements, so both renderers and the data-eye lookups stay as
// they are. Centres (46.3,11) and (53.7,11), radius 1.8.
const MASK = {
  mask: 'M42,11 a8,9 0 0 1 16,0 L57,16 Q50,25 43,16 Z',
  fill: '#ece9e1',
  rim: '#2a2f36',
  eyes: [
    'M44.5,11 a1.8,1.8 0 1,0 3.6,0 a1.8,1.8 0 1,0 -3.6,0 Z',
    'M51.9,11 a1.8,1.8 0 1,0 3.6,0 a1.8,1.8 0 1,0 -3.6,0 Z'
  ],
  eyeFill: '#d11a1a',
  // [cx, cy] - a loose triangle under the eyes, then the cheeks.
  vents: [[47, 15.2], [50, 15.6], [53, 15.2], [48.5, 17.4], [51.5, 17.4], [50, 19.2], [45, 13.6], [55, 13.6]],
  ventFill: '#2a2a2a',
  ventR: 0.42
};

const SLASHER_FRAMES = [
  // Upright, unhurried. One arm out ahead with the machete; the other hangs.
  { paths: ['M50,20 L50,56', 'M50,30 L61,50', 'M50,30 L28,34', 'M50,56 L42,80 L40,96', 'M50,56 L58,80 L62,96'], ...MACHETE, ...MASK },
  { paths: ['M50,20 L50,56', 'M50,30 L60,50', 'M50,30 L28,34', 'M50,56 L46,80 L48,96', 'M50,56 L55,80 L54,96'], ...MACHETE, ...MASK }
];

// A stepped conifer silhouette in a 100x100 box. `notch` (roughly 0.18-0.32)
// is how far each tier cuts back in, so no two trunks share a profile.
const coniferPath = (notch) => {
  const n = Math.round(notch * 100);
  return [
    'M50,0',
    `L72,28 L${72 - n},26 L80,52 L${80 - n},50 L88,78 L${88 - n},76 L92,100`,
    `L8,100 L${12 + n},76 L12,78 L${20 + n},50 L20,52 L${28 + n},26 L28,28 Z`
  ].join(' ');
};

// Clamp the frame delta so returning to a backgrounded tab doesn't teleport
// every creature across the screen in a single step.
const MAX_FRAME_MS = 100;

const MAX_BATS = 12;

// Creature ids come from a counter, not from `Date.now() + Math.random()`.
// That sum looked unique and was not: at 1.8 trillion a double has about
// twelve bits left for the fraction, so the random part is quantised to ~4000
// values per millisecond. Tomb shadows spawn three to six in the same
// millisecond, which made a collision - and React's "two children with the
// same key" - a matter of minutes. Integers also keep `id % n` meaningful
// where it picks an animation duration or a shadow's shape.
let creatureSeq = 0;
const nextCreatureId = () => ++creatureSeq;

// The haunted house's fireplace width, from which its height (3:4) and the
// cloaked man's stature are both derived, so "twice the height of the
// fireplace" holds at every viewport width.
export const FIREPLACE_WIDTH = 'clamp(220px, 24vw, 380px)';
export const fireplaceHeights = (n) => `calc(${FIREPLACE_WIDTH} * 0.75 * ${n})`;

// The bookcase: shelves of books laid out from the index rather than the
// dice, so the same room greets every visit. Widths, heights and colours
// cycle at different periods, which is enough to keep any shelf from
// repeating. A few lean; a few gaps are left.
const BOOK_COLOURS = ['#5a1f1f', '#3b4a2f', '#24324a', '#6b4a2b', '#4a3a5a', '#2f2f2f', '#7a5a2a', '#3d2a1a'];
const layOutBooks = (shelves, shelfWidth) => {
  const out = [];
  for (let s = 0; s < shelves; s++) {
    let x = 4;
    let i = 0;
    while (x < shelfWidth - 8) {
      const n = s * 13 + i;
      const w = 7 + (n * 5) % 9;
      const h = 22 + (n * 7) % 14;
      const gap = (n % 11 === 7) ? 9 : 0;      // the odd missing book
      if (x + w + gap > shelfWidth - 4) break;
      out.push({
        shelf: s, x: x + gap, w, h,
        colour: BOOK_COLOURS[(n * 3) % BOOK_COLOURS.length],
        lean: (n % 9 === 4) ? -7 : 0            // leaning against its neighbour
      });
      x += w + gap + 1;
      i++;
    }
  }
  return out;
};

// A gilt frame round a dark canvas, for the paintings on the house's wall.
// `id` keeps the gradient ids apart; `light` is how much of the fire's glow
// reaches it from below.
const GiltFrame = ({ id, light = 0.2, svgRef, children }) => (
  <svg ref={svgRef} viewBox="0 0 120 150" width="100%" style={{ display: 'block' }}>
    <defs>
      <radialGradient id={`${id}-vignette`} cx="50%" cy="45%" r="60%">
        <stop offset="0.45" stopColor="#000" stopOpacity="0" />
        <stop offset="1" stopColor="#000" stopOpacity="0.75" />
      </radialGradient>
      <linearGradient id={`${id}-firelight`} x1="0" y1="1" x2="0" y2="0">
        <stop offset="0" stopColor="#ffb060" stopOpacity={light} />
        <stop offset="0.6" stopColor="#ffb060" stopOpacity="0" />
      </linearGradient>
      {/* The sitter is painted on the canvas and nowhere else: nothing of
          them can run over the frame. */}
      <clipPath id={`${id}-canvas`}>
        <rect x="11" y="11" width="98" height="128" />
      </clipPath>
    </defs>
    <rect data-frame x="0" y="0" width="120" height="150" rx="2" fill="#6b5120" />
    <rect x="3" y="3" width="114" height="144" fill="none" stroke="#8f6d2a" strokeWidth="2" />
    <rect x="8" y="8" width="104" height="134" fill="none" stroke="#3f2d10" strokeWidth="2" />
    {[[6, 6], [114, 6], [6, 144], [114, 144]].map(([cx, cy]) => (
      <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#a07f34" />
    ))}
    <rect x="11" y="11" width="98" height="128" fill="#15100a" />
    <rect x="11" y="11" width="98" height="128" fill="#2a1d10" opacity="0.5" />
    <g data-canvas clipPath={`url(#${id}-canvas)`}>{children}</g>
    <rect x="11" y="11" width="98" height="128" fill={`url(#${id}-firelight)`} />
    <rect x="11" y="11" width="98" height="128" fill={`url(#${id}-vignette)`} />
  </svg>
);

// The sitters for the two further portraits. Each is a function of the
// refs it may need; the woman's pupils are handed to the gaze effect.
const PORTRAIT_SITTERS = {
  // A young woman, severe: hair scraped back to a knot, a long pale face,
  // thin arched brows, narrow eyes, a mouth held in a line; a high dark
  // dress with a lace collar and a brooch at the throat.
  woman: ({ gazeNode }) => (
    <>
      <path d="M22,139 Q30,104 60,100 Q90,104 98,139 Z" fill="#141018" />
      <path d="M48,101 Q60,111 72,101 L70,96 Q60,104 50,96 Z" fill="#e8e2d6" />
      <rect x="54" y="84" width="12" height="18" fill="#cdb79c" />
      <circle cx="60" cy="105" r="3.2" fill="#8a6a2a" />
      <circle cx="60" cy="105" r="1.6" fill="#d9c7b0" />
      <ellipse cx="60" cy="48" rx="23" ry="29" fill="#1a1410" />
      <circle cx="60" cy="21" r="8" fill="#1a1410" />
      <path data-face d="M44,50 Q44,26 60,26 Q76,26 76,50 Q77,74 68,86 Q60,92 52,86 Q43,74 44,50 Z" fill="#d9c7b0" />
      <path d="M44,54 Q43,32 55,27 Q48,34 47,54 Z" fill="#1a1410" />
      <path d="M76,54 Q77,32 65,27 Q72,34 73,54 Z" fill="#1a1410" />
      <path d="M60,26 L60,31" stroke="#1a1410" strokeWidth="1.2" />
      <ellipse cx="48" cy="63" rx="4" ry="7" fill="#a88870" opacity="0.35" />
      <ellipse cx="72" cy="63" rx="4" ry="7" fill="#a88870" opacity="0.35" />
      <path d="M48,46 Q53,41 58,45" fill="none" stroke="#2a1a14" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M62,45 Q67,41 72,46" fill="none" stroke="#2a1a14" strokeWidth="1.4" strokeLinecap="round" />
      <ellipse data-eye cx="52.5" cy="50" rx="4" ry="2.2" fill="#f0ebe2" />
      <ellipse data-eye cx="67.5" cy="50" rx="4" ry="2.2" fill="#f0ebe2" />
      {/* Her pupils, in a group per eye the gaze effect moves. */}
      {[53, 68].map((cx, i) => (
        <g key={cx} data-gaze={i} ref={gazeNode ? gazeNode(i) : undefined}>
          <circle cx={cx} cy="50.2" r="1.5" fill="#0a0a0a" />
        </g>
      ))}
      <path d="M48.5,49 Q52.5,47 56.5,49" fill="none" stroke="#2a1a14" strokeWidth="0.8" />
      <path d="M63.5,49 Q67.5,47 71.5,49" fill="none" stroke="#2a1a14" strokeWidth="0.8" />
      <path d="M60,50 L57,64 L63,64 Z" fill="#c4ac92" />
      <path d="M53,74 L67,74" stroke="#6a3a3a" strokeWidth="1.4" strokeLinecap="round" />
    </>
  ),
  // A knight, visor down: a steel helm with a dark slit and nothing to be
  // seen through it but two points of light, a red plume, pauldrons, a
  // gorget, a red tabard, and a sword's hilt held before the chest.
  knight: () => (
    <>
      <path d="M20,139 Q28,100 60,96 Q92,100 100,139 Z" fill="#5a1f22" />
      <path d="M26,112 Q30,97 48,99 L50,114 Q38,120 26,118 Z" fill="#8a8f96" />
      <path d="M94,112 Q90,97 72,99 L70,114 Q82,120 94,118 Z" fill="#7a7f86" />
      <path d="M30,104 Q38,100 46,102" fill="none" stroke="#c0c5cc" strokeWidth="1.2" opacity="0.7" />
      <path d="M46,97 Q60,105 74,97 L72,89 Q60,95 48,89 Z" fill="#7a7f86" />
      {/* The plume sweeps back and down, well inside the canvas (y >= 14). */}
      <path data-plume d="M60,24 Q54,14 42,15 Q50,17 56,26 Z" fill="#7a2a2a" />
      <path data-helm d="M42,46 Q42,20 60,20 Q78,20 78,46 L78,74 Q78,88 60,90 Q42,88 42,74 Z" fill="#8a8f96" />
      <path d="M42,57 L78,57 L78,74 Q78,86 60,88 Q42,86 42,74 Z" fill="#6e737a" />
      <path d="M60,20 L60,50" stroke="#c0c5cc" strokeWidth="1.5" />
      <path d="M46,30 Q44,44 46,56" fill="none" stroke="#c9ced4" strokeWidth="2" opacity="0.6" />
      <rect data-visor x="46" y="51.5" width="28" height="4.5" rx="2" fill="#050505" />
      <circle data-eye cx="54" cy="53.7" r="0.9" fill="#d9d4c8" opacity="0.85" />
      <circle data-eye cx="66" cy="53.7" r="0.9" fill="#d9d4c8" opacity="0.85" />
      {[[54, 70], [60, 72], [66, 70]].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="1.1" fill="#2a2d31" />
      ))}
      <rect x="58" y="104" width="4" height="28" fill="#c9ced4" />
      <rect x="58" y="113" width="4" height="13" fill="#3a2a1a" />
      <rect x="46" y="108" width="28" height="4" rx="1" fill="#a07f34" />
      <circle cx="60" cy="133" r="3.5" fill="#a07f34" />
    </>
  )
};

// Which file shows a walker facing a given way, and whether it must be
// mirrored because only the opposite side was drawn. A missing quarter turn
// falls back to the straight-on file rather than skipping the turn.
export const walkerSprite = (images = {}, facing) => {
  if (images[facing]) return { src: images[facing], flip: false };
  const opposite = { east: 'west', west: 'east', southEast: 'southWest', southWest: 'southEast' }[facing];
  if (opposite && images[opposite]) return { src: images[opposite], flip: true };
  if ((facing === 'southEast' || facing === 'southWest') && images.south) {
    return { src: images.south, flip: false };
  }
  return { src: images.west || images.east || images.south || null, flip: false };
};

const AnimatedBackground = ({ theme }) => {
  // React state holds the *roster* only - which creatures exist. Positions live
  // in refs and are written straight to the DOM, so movement never re-renders.
  const [bats, setBats] = useState([]);
  const [mothman, setMothman] = useState(null);
  const [eyes, setEyes] = useState([]);
  const [tombShadows, setTombShadows] = useState([]);
  const [sarcophagus, setSarcophagus] = useState(null);

  const batsRef = useRef([]);
  const mothmanRef = useRef(null);
  const eyesRef = useRef([]);
  const tombShadowsRef = useRef([]);
  const sarcophagusRef = useRef(null);

  // id -> DOM node, populated by ref callbacks during render
  const batNodes = useRef(new Map());
  const eyeNodes = useRef(new Map());
  const shadowNodes = useRef(new Map());
  const shadowEllipseNodes = useRef(new Map());
  const mothmanNode = useRef(null);
  const sarcophagusNode = useRef(null);

  // The chase: one pair at a time. Roster in state, positions in refs, like
  // everything else here. The frame-node arrays hold the two <g> gait frames
  // per figure, so the loop can flip them without a re-render.
  const [chase, setChase] = useState(null);
  const chaseRef = useRef(null);
  const teenNode = useRef(null);
  const slasherNode = useRef(null);
  const teenFrameNodes = useRef([]);
  const slasherFrameNodes = useRef([]);

  // The walker: one animated GIF figure crossing along the ground band. The
  // browser runs the GIF's frames by itself; the loop only moves the box.
  const [walker, setWalker] = useState(null);
  const walkerRef = useRef(null);
  const walkerNode = useRef(null);

  // The ghosts: a few at a time, drawn in SVG. Roster in state, everything
  // that moves in refs, like the rest.
  const [ghosts, setGhosts] = useState([]);
  const ghostsRef = useRef([]);
  const ghostNodes = useRef(new Map());

  // The cloaked man: one at a time, along the floor.
  const [cloaked, setCloaked] = useState(null);
  const cloakedRef = useRef(null);
  const cloakedNode = useRef(null);

  // The jump scare: one shot, rare, brief. State only - it never touches the
  // frame loop, and it is removed by a timeout rather than animated away,
  // because the hard cut-out is the scare.
  const [scare, setScare] = useState(null);

  // Get creature configs from theme (with fallbacks to preserve exact current behavior)
  const batConfig = theme.creatures?.bats || {};
  const mothmanConfig = theme.creatures?.mothman || {};
  const eyesConfig = theme.creatures?.eyes || {};
  const tombShadowConfig = theme.creatures?.tombShadows || {};
  const sarcophagusConfig = theme.creatures?.sarcophagus || {};
  const chaseConfig = theme.creatures?.chase || {};
  const jumpScareConfig = theme.creatures?.jumpScare || {};
  const walkerConfig = theme.creatures?.walker || {};
  const ghostConfig = theme.creatures?.ghosts || {};
  const windowsConfig = theme.creatures?.windows || {};
  const fireplaceConfig = theme.creatures?.fireplace || {};
  const candlesConfig = theme.creatures?.candles || {};
  const bookshelvesConfig = theme.creatures?.bookshelves || {};
  const armchairConfig = theme.creatures?.armchair || {};
  const cloakedConfig = theme.creatures?.cloakedMan || {};
  const portraitConfig = theme.creatures?.portrait || {};
  const portraitsConfig = theme.creatures?.portraits || {};
  const ravenConfig = theme.creatures?.raven || {};
  const doorConfig = theme.creatures?.secretDoor || {};

  // The secret door: shut, open, or closing - the last being the beat
  // before it shuts when the hand withdraws and the eyes go out. Its
  // opener lives with the other schedulers further down, after
  // reducedMotion exists.
  const [doorPhase, setDoorPhase] = useState('shut');
  const doorOpen = doorPhase !== 'shut';

  // Which side of the interface the door is on. Shut and at rest it is part
  // of the wall, behind the calendar like the rest of the room. When it
  // opens it comes to the FRONT, over everything but the jump scare, so
  // the dark, the eyes and the hand are never hidden by a calendar block.
  // When it shuts it stays in front while the panel swings closed, then
  // fades out there and fades back in behind - it sinks into the wall.
  const [doorLayer, setDoorLayer] = useState('back');
  const [doorSinking, setDoorSinking] = useState(false);
  useEffect(() => {
    if (doorPhase !== 'shut') {
      setDoorSinking(false);
      setDoorLayer('front');
      return undefined;
    }
    if (doorLayer === 'back') return undefined;
    const swing = doorConfig.swing ?? 1600;   // must match the panel's CSS transition
    const sink = doorConfig.sink ?? 700;      // must match .hh-door's opacity transition
    let t = setTimeout(() => {
      setDoorSinking(true);
      t = setTimeout(() => {
        setDoorLayer('back');
        setDoorSinking(false);
      }, sink);
    }, swing);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doorPhase]);

  // Latest-value ref so the animation loop always reads current config without
  // having to tear down and rebuild the rAF loop on every render.
  const configRef = useRef(null);
  configRef.current = { batConfig, mothmanConfig, eyesConfig, tombShadowConfig, sarcophagusConfig, chaseConfig, walkerConfig, ghostConfig, cloakedConfig };

  // The books, laid out once per theme.
  const books = useMemo(
    () => (bookshelvesConfig.enabled ? layOutBooks(bookshelvesConfig.shelves || 5, 192) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.id]
  );

  // The candles: scenery, placed from config so they hang in the same spots
  // every visit. Each bobs on its own CSS animation; the timings come from
  // the index so no two share a rhythm, and the negative delays mean none of
  // them start in step.
  const candles = useMemo(() => {
    if (!candlesConfig.enabled) return [];
    return (candlesConfig.positions || []).map(([x, y, size], i) => ({
      id: i,
      leftPct: x * 100,
      topPct: y * 100,
      size: size ?? 1,
      duration: 3.6 + (i % 4) * 0.7,
      delay: -(i * 0.9),
      flicker: 0.42 + (i % 3) * 0.09
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id]);

  // Lightning: a strike is a short-lived id; while it is set, the windows
  // and the room carry a flash element whose CSS runs the blink sequence.
  const [lightning, setLightning] = useState(null);

  // The portraits' eyes follow the cursor. Written straight to the DOM, one
  // frame at a time, so a mouse that never stops moving never re-renders
  // anything. Each watcher's pupils deflect within their sockets toward the
  // pointer: fully once it is more than ~160px away, proportionally inside
  // that. The old man's sockets allow more travel than her narrow eyes.
  const portraitSvg = useRef(null);
  const gazeNodes = useRef([]);
  const womanSvg = useRef(null);
  const womanGazeNodes = useRef([]);
  const watching = (portraitConfig.enabled && fireplaceConfig.enabled) || portraitsConfig.enabled;
  useEffect(() => {
    if (!watching) return undefined;

    const WATCHERS = [
      { svg: portraitSvg, nodes: gazeNodes, eyes: [[52.5, 52.5], [70.5, 52.5]], reach: 2.4, vert: 0.7 },
      { svg: womanSvg, nodes: womanGazeNodes, eyes: [[52.5, 50], [67.5, 50]], reach: 1.7, vert: 0.45 }
    ];
    let cursor = null;
    let frame = null;

    const look = () => {
      frame = null;
      if (!cursor) return;
      WATCHERS.forEach(({ svg: svgRef, nodes, eyes, reach, vert }) => {
        const svg = svgRef.current;
        if (!svg) return;
        const r = svg.getBoundingClientRect();
        if (!r.width || !r.height) return;
        eyes.forEach(([cx, cy], i) => {
          const node = nodes.current[i];
          if (!node) return;
          const ex = r.left + (cx / 120) * r.width;
          const ey = r.top + (cy / 150) * r.height;
          const dx = cursor.x - ex;
          const dy = cursor.y - ey;
          const dist = Math.hypot(dx, dy) || 1;
          const k = Math.min(1, dist / 160) * reach;
          node.setAttribute('transform', `translate(${((dx / dist) * k).toFixed(2)} ${((dy / dist) * k * vert).toFixed(2)})`);
        });
      });
    };
    const onMove = (e) => {
      cursor = { x: e.clientX, y: e.clientY };
      if (frame === null) frame = requestAnimationFrame(look);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (frame !== null) cancelAnimationFrame(frame);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, watching]);

  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReducedMotion(e.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // Create a bat with properties from theme config
  const createBat = () => {
    if (!batConfig.enabled) return null;

    const direction = Math.random() < 0.5 ? -1 : 1;
    const startX = direction === -1 ? window.innerWidth + 150 : -150;

    return {
      id: nextCreatureId(),
      x: startX,
      startX, // erratic flight derives x from age, so it needs the origin
      y: Math.random() * (window.innerHeight * 0.8) + window.innerHeight * 0.1,
      targetY: Math.random() * (window.innerHeight * 0.6) + window.innerHeight * 0.2,
      direction: direction,
      speed: Math.random() * (batConfig.speed?.max - batConfig.speed?.min || 4.0) + (batConfig.speed?.min || 6.0),
      size: Math.random() * (batConfig.size?.max - batConfig.size?.min || 0.4) + (batConfig.size?.min || 0.8),
      age: 0,
      wingPhase: Math.random() * Math.PI * 2,
      // Seeds the wander noise, so no two moths trace the same path.
      seed: Math.random() * 1000,
      // Wingbeat in radians per ms, jittered so a swarm does not pulse in unison.
      flapRate: 0.018 + Math.random() * 0.010
    };
  };

  // The lot: cars parked facing the screen, so all that shows is their backs
  // and their tail lights.
  //
  // Generated once per theme rather than spawned on a timer - they are parked,
  // and a row that shuffled itself every few seconds would read as traffic.
  // Nothing here moves, so it never touches the animation loop.
  const parkedCars = useMemo(() => {
    const cfg = theme.creatures?.parkedCars;
    if (!cfg?.enabled) return [];

    const colors = cfg.colors || ['#0b0d14', '#101320', '#0a0c12'];
    const rows = cfg.rows || 2;
    const out = [];

    for (let row = 0; row < rows; row++) {
      // Row 0 is the back of the lot: smaller, higher up, dimmer.
      const depth = rows === 1 ? 1 : row / (rows - 1);
      const count = Math.round(6 + depth * 2 + Math.random() * 2);

      for (let i = 0; i < count; i++) {
        const jitter = (Math.random() - 0.5) * (60 / count);
        out.push({
          id: `${row}-${i}`,
          // Spread across the width with a little slop, so the row is not a grid.
          leftPct: (i + 0.5) * (100 / count) + jitter,
          width: (cfg.width?.min || 110) + depth * ((cfg.width?.max || 190) - (cfg.width?.min || 110))
            + Math.random() * 20,
          // Clear of the letterbox bar, which is 36px tall. Sitting the front
          // row lower hid its tail lights behind it entirely.
          bottom: 34 + (1 - depth) * 34,
          color: colors[Math.floor(Math.random() * colors.length)],
          opacity: 0.45 + depth * 0.5,
          glow: (cfg.glow?.min || 8) + depth * ((cfg.glow?.max || 16) - (cfg.glow?.min || 8)),
          // A few cars have a brake light on, or one bulb gone.
          litLeft: Math.random() > 0.15,
          litRight: Math.random() > 0.15,
          zIndex: -22 + row
        });
      }
    }
    return out;
    // Re-rolled only when the theme changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id]);

  // The treeline: jagged conifer silhouettes along the foot of the frame, the
  // forest's equivalent of the drive-in's parked lot. Scenery, so it is built
  // once per theme, never moves, and never touches the animation loop.
  const treeline = useMemo(() => {
    const cfg = theme.creatures?.treeline;
    if (!cfg?.enabled) return [];

    const colors = cfg.colors || ['#0b140e', '#0f1a12', '#08100b'];
    const rows = cfg.rows || 2;
    const hMin = cfg.height?.min || 90;
    const hMax = cfg.height?.max || 220;
    const out = [];

    for (let row = 0; row < rows; row++) {
      // Row 0 is the far edge of the wood: shorter, dimmer, set back.
      const depth = rows === 1 ? 1 : row / (rows - 1);
      const count = Math.round(9 + depth * 3 + Math.random() * 3);

      for (let i = 0; i < count; i++) {
        const jitter = (Math.random() - 0.5) * (70 / count);
        const height = hMin + depth * (hMax - hMin) + Math.random() * 30;
        out.push({
          id: `${row}-${i}`,
          leftPct: (i + 0.5) * (100 / count) + jitter,
          width: height * (0.32 + Math.random() * 0.16),
          height,
          lean: (Math.random() - 0.5) * 6, // degrees - a wood is never plumb
          // Three notch depths, so no two trunks share a profile.
          notch: 0.18 + Math.random() * 0.14,
          color: colors[Math.floor(Math.random() * colors.length)],
          opacity: 0.55 + depth * 0.45,
          zIndex: -24 + row
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id]);

  // Create Mothman with properties from theme config
  const createMothman = () => {
    if (!mothmanConfig.enabled) return null;

    const direction = Math.random() < 0.5 ? -1 : 1;
    const startX = direction === -1 ? window.innerWidth + 150 : -150;

    return {
      id: nextCreatureId(),
      x: startX,
      y: Math.random() * (window.innerHeight * 0.8) + window.innerHeight * 0.1,
      targetY: Math.random() * (window.innerHeight * 0.6) + window.innerHeight * 0.2,
      direction: direction,
      speed: mothmanConfig.speed || 30.0,
      age: 0
    };
  };

  // Create glowing eyes for Dark Forest theme
  const createEyes = () => {
    if (!eyesConfig.enabled) return null;

    const colors = eyesConfig.colors || ['#ff4444', '#44ff44', '#ffff44'];
    const color = colors[Math.floor(Math.random() * colors.length)];

    return {
      id: nextCreatureId(),
      x: Math.random() * window.innerWidth,
      y: Math.random() * (window.innerHeight * 0.7) + window.innerHeight * 0.15, // Avoid very top/bottom
      color: color,
      size: Math.random() * (eyesConfig.size?.max - eyesConfig.size?.min || 0.7) + (eyesConfig.size?.min || 0.8),
      opacity: Math.random() * (eyesConfig.opacity?.max - eyesConfig.opacity?.min || 0.3) + (eyesConfig.opacity?.min || 0.6),
      glowIntensity: Math.random() * (eyesConfig.glowIntensity?.max - eyesConfig.glowIntensity?.min || 7) + (eyesConfig.glowIntensity?.min || 8),
      age: 0,
      blinkPhase: Math.random() * Math.PI * 2,
      isBlinking: false,
      blinkDuration: 0
    };
  };

  // Create tomb shadow for Ancient Tomb theme
  const createTombShadow = () => {
    if (!tombShadowConfig.enabled) return null;

    const colors = tombShadowConfig.colors || ['#1a1a1a', '#2d2d2d', '#0d0d0d'];
    const color = colors[Math.floor(Math.random() * colors.length)];

    // Shadows spawn from various positions to create walking effect
    const spawnSide = Math.random();
    let startX, startY;

    if (spawnSide < 0.4) {
      // 40% spawn from left (walking direction)
      startX = -50;
      startY = Math.random() * window.innerHeight * 0.8 + window.innerHeight * 0.1;
    } else if (spawnSide < 0.7) {
      // 30% spawn from bottom (ground shadows)
      startX = Math.random() * window.innerWidth;
      startY = window.innerHeight + 20;
    } else {
      // 30% spawn from right (reverse walking)
      startX = window.innerWidth + 50;
      startY = Math.random() * window.innerHeight * 0.8 + window.innerHeight * 0.1;
    }

    return {
      id: nextCreatureId(),
      x: startX,
      y: startY,
      color: color,
      size: Math.random() * (tombShadowConfig.size?.max - tombShadowConfig.size?.min || 40) + (tombShadowConfig.size?.min || 20),
      opacity: Math.random() * (tombShadowConfig.opacity?.max - tombShadowConfig.opacity?.min || 0.2) + (tombShadowConfig.opacity?.min || 0.1),
      speed: Math.random() * (tombShadowConfig.speed?.max - tombShadowConfig.speed?.min || 2) + (tombShadowConfig.speed?.min || 1),
      flickerIntensity: Math.random() * (tombShadowConfig.flickerIntensity?.max - tombShadowConfig.flickerIntensity?.min || 2) + (tombShadowConfig.flickerIntensity?.min || 1),
      age: 0,
      walkPhase: Math.random() * Math.PI * 2,
      direction: spawnSide < 0.4 ? 1 : spawnSide < 0.7 ? 0 : -1, // 1 = right, 0 = up, -1 = left
      lifespan: Math.random() * (tombShadowConfig.lifespan?.max - tombShadowConfig.lifespan?.min || 7000) + (tombShadowConfig.lifespan?.min || 5000)
    };
  };

  // Create sarcophagus for Ancient Tomb theme
  // The chase: a teen at full sprint, the killer walking behind and still
  // gaining. One pair at a time, rarely - it is the set piece. The teen starts
  // just off-screen and the killer trails by gapStart, so it enters a beat
  // later, which is what makes it read as a pursuit rather than two sprites.
  const createChase = () => {
    if (!chaseConfig.enabled) return null;

    const direction = Math.random() < 0.5 ? -1 : 1;
    const yMin = chaseConfig.y?.min ?? 0.55;
    const yMax = chaseConfig.y?.max ?? 0.78;

    return {
      id: nextCreatureId(),
      direction,
      startX: direction === -1 ? window.innerWidth + 120 : -120,
      baseY: window.innerHeight * (yMin + Math.random() * (yMax - yMin)),
      speed: chaseConfig.speed || 7.0,
      gapStart: chaseConfig.gap?.start ?? 240,
      gapEnd: chaseConfig.gap?.end ?? 120,
      scale: chaseConfig.scale || 1.1,
      lifespan: chaseConfig.lifespan || 30000,
      // Relative height. The killer is a little taller than the person he is
      // after; both scale from their feet so he grows up, not down.
      teenStature: chaseConfig.stature?.teen ?? 1.0,
      slasherStature: chaseConfig.stature?.slasher ?? 1.12,
      age: 0
    };
  };

  // The walker enters just off one edge and crosses at a steady shamble.
  // direction -1 heads west (left). It may stop once on the way, somewhere in
  // the middle of the screen, turn to face the room, stare, and turn back:
  // pauseAt is the age at which it stops, pauseFor how long it stands.
  const createWalker = () => {
    if (!walkerConfig.enabled) return null;

    const direction = Math.random() < 0.5 ? -1 : 1;
    const yMin = walkerConfig.y?.min ?? 0.55;
    const yMax = walkerConfig.y?.max ?? 0.78;
    const startX = direction === -1 ? window.innerWidth + 150 : -150;
    const speed = walkerConfig.speed || 1.8;
    const baseY = window.innerHeight * (yMin + Math.random() * (yMax - yMin));

    const pauseCfg = walkerConfig.pause || {};
    const turn = pauseCfg.turn ?? 350;
    let pauseAt = null;
    let pauseFor = 0;
    // Only with a straight-on file to show: without one there is nothing to
    // turn to, so it just keeps walking.
    const canPause = Boolean(walkerConfig.images?.south) && (pauseCfg.enabled ?? true);
    if (canPause && Math.random() < (pauseCfg.chance ?? 0.7)) {
      const stopX = window.innerWidth * (0.3 + Math.random() * 0.4);
      pauseAt = (Math.abs(stopX - startX) / speed) * TICK_MS;
      const holdMin = pauseCfg.hold?.min ?? 1500;
      const holdMax = pauseCfg.hold?.max ?? 3000;
      pauseFor = turn + holdMin + Math.random() * (holdMax - holdMin) + turn;
    }

    const facing = direction === -1 ? 'west' : 'east';
    return {
      id: nextCreatureId(),
      direction,
      startX,
      baseY,
      speed,
      scale: walkerConfig.scale || 1.0,
      lifespan: walkerConfig.lifespan || 100000,
      pauseAt,
      pauseFor,
      turn,
      facing,
      flip: walkerSprite(walkerConfig.images, facing).flip,
      age: 0
    };
  };

  // A ghost enters just off one edge, somewhere in the upper half of the
  // frame, and drifts across at its own pace. Some are given a point in the
  // hall at which they simply stop being there.
  const createGhost = () => {
    if (!ghostConfig.enabled) return null;

    const direction = Math.random() < 0.5 ? -1 : 1;
    const yMin = ghostConfig.y?.min ?? 0.15;
    const yMax = ghostConfig.y?.max ?? 0.65;
    const speedMin = ghostConfig.speed?.min ?? 1.6;
    const speedMax = ghostConfig.speed?.max ?? 2.6;
    const scaleMin = ghostConfig.scale?.min ?? 0.8;
    const scaleMax = ghostConfig.scale?.max ?? 1.4;
    const startX = direction === -1 ? window.innerWidth + 160 : -160;
    const speed = speedMin + Math.random() * (speedMax - speedMin);

    let vanishAt = null;
    if (Math.random() < (ghostConfig.vanish?.chance ?? 0.4)) {
      // Somewhere in the middle of the hall, expressed as the age at which
      // the drift reaches that point.
      const stopX = window.innerWidth * (0.3 + Math.random() * 0.4);
      vanishAt = (Math.abs(stopX - startX) / speed) * TICK_MS;
    }

    return {
      id: nextCreatureId(),
      direction,
      startX,
      baseY: window.innerHeight * (yMin + Math.random() * (yMax - yMin)),
      speed,
      scale: scaleMin + Math.random() * (scaleMax - scaleMin),
      phase: Math.random() * Math.PI * 2,
      vanishAt,
      lifespan: ghostConfig.lifespan || 100000,
      age: 0
    };
  };

  // The cloaked man enters just off one edge and crosses along the floor.
  // direction -1 heads west (left), the way he is drawn.
  const createCloaked = () => {
    if (!cloakedConfig.enabled) return null;
    const direction = Math.random() < 0.5 ? -1 : 1;
    return {
      id: nextCreatureId(),
      direction,
      startX: direction === -1 ? window.innerWidth + 300 : -300,
      speed: cloakedConfig.speed || 1.4,
      lifespan: cloakedConfig.lifespan || 150000,
      age: 0
    };
  };

  const createSarcophagus = () => {
    if (!sarcophagusConfig.enabled) return null;

    // Sarcophagus can appear from any side
    const startSide = sarcophagusConfig.positioning?.startSide === 'random' ? Math.random() : 0.5;
    let startX, startY;

    if (startSide < 0.25) {
      // Left side
      startX = -100;
      startY = Math.random() * window.innerHeight * 0.6 + window.innerHeight * 0.2;
    } else if (startSide < 0.5) {
      // Right side
      startX = window.innerWidth + 100;
      startY = Math.random() * window.innerHeight * 0.6 + window.innerHeight * 0.2;
    } else if (startSide < 0.75) {
      // Top
      startX = Math.random() * window.innerWidth * 0.6 + window.innerWidth * 0.2;
      startY = -100;
    } else {
      // Bottom
      startX = Math.random() * window.innerWidth * 0.6 + window.innerWidth * 0.2;
      startY = window.innerHeight + 100;
    }

    return {
      id: nextCreatureId(),
      x: startX,
      y: startY,
      scale: sarcophagusConfig.scale || 2.5,
      opacity: sarcophagusConfig.opacity || 0.4,
      speed: sarcophagusConfig.speed || 0.5,
      age: 0,
      lifespan: sarcophagusConfig.lifespan || 25000,
      direction: startSide < 0.25 ? 1 : startSide < 0.5 ? -1 : startSide < 0.75 ? 0.5 : -0.5,
      filter: sarcophagusConfig.filter || 'drop-shadow(0 0 8px rgba(139, 69, 19, 0.6)) brightness(0.6) contrast(1.2)'
    };
  };

  // Reset every roster when the theme changes so creatures from the previous
  // theme don't linger in the refs.
  useEffect(() => {
    batsRef.current = [];
    mothmanRef.current = null;
    eyesRef.current = [];
    tombShadowsRef.current = [];
    sarcophagusRef.current = null;
    walkerRef.current = null;
    ghostsRef.current = [];
    cloakedRef.current = null;

    setBats([]);
    setMothman(null);
    setEyes([]);
    setTombShadows([]);
    setSarcophagus(null);
    setWalker(null);
    setGhosts([]);
    setCloaked(null);
  }, [theme.id]);

  // ---------------------------------------------------------------------------
  // Animation loop
  //
  // Mutates creature objects in place and writes transforms directly to their
  // DOM nodes. React state is only touched when a creature expires and needs to
  // unmount, which happens on the order of once every few seconds rather than
  // 20 times per second.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (reducedMotion) return;

    let frameId = null;
    let lastTime = null;

    const tick = (now) => {
      frameId = requestAnimationFrame(tick);

      if (lastTime === null) {
        lastTime = now;
        return;
      }

      const dt = Math.min(now - lastTime, MAX_FRAME_MS);
      lastTime = now;
      if (dt <= 0) return;

      // How many 50ms reference ticks this frame represents.
      const steps = dt / TICK_MS;

      const {
        batConfig: bats_,
        mothmanConfig: moth_,
        eyesConfig: eyes_,
        tombShadowConfig: shadows_,
        sarcophagusConfig: sarc_,
        chaseConfig: chase_,
        walkerConfig: walker_,
        ghostConfig: ghost_,
        cloakedConfig: cloaked_
      } = configRef.current;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // ---- Bats -------------------------------------------------------------
      if (bats_.enabled && batsRef.current.length > 0) {
        const survivors = [];

        const erratic = bats_.flight === 'erratic';
        const flap = bats_.wingFlap;

        for (const bat of batsRef.current) {
          bat.age += dt;

          if (erratic) {
            // A moth does not commit to a heading. Three timescales layered:
            // long swerves, ordinary weaving, and a fine jitter on top.
            const slow = wander(bat.age, bat.seed, 0.0016);
            const mid = wander(bat.age, bat.seed + 11, 0.011);
            const fast = wander(bat.age, bat.seed + 23, 0.045);

            // Position is derived from age rather than accumulated per frame.
            // Modulating velocity instead would make x a numerical integral,
            // so its value would depend slightly on frame rate - and this file
            // guarantees identical motion at any frame rate.
            //
            // A wander offset on x is what surging and stalling looks like: it
            // outruns the steady drift, then falls behind it, and a big swerve
            // carries it briefly backwards.
            bat.x = bat.startX
              + bat.direction * bat.speed * (bat.age / TICK_MS)
              + slow * 90 + mid * 28;

            bat.y = bat.targetY
              + slow * (bats_.wobble?.amplitude || 20) * 3.5
              + mid * (bats_.flutter?.amplitude || 25) * 1.2
              + fast * 6;
          } else {
            bat.x += bat.direction * bat.speed * steps;

            const flutter = Math.sin(bat.age * (bats_.flutter?.frequency || 0.01)) * (bats_.flutter?.amplitude || 15);
            const wobble = Math.sin(bat.age * (bats_.wobble?.frequency || 0.005)) * (bats_.wobble?.amplitude || 30);
            bat.y = bat.targetY + flutter + wobble;
          }

          bat.wingPhase += 0.3 * steps;

          const isOffScreen = bat.direction === -1 ? bat.x < -200 : bat.x > viewportWidth + 200;
          const isTooOld = bat.age > (bats_.lifespan || 12000);

          if (isOffScreen || isTooOld) continue;

          survivors.push(bat);

          const node = batNodes.current.get(bat.id);
          if (node) {
            // Wings, faked by squashing horizontally. abs(sin) snaps rather
            // than sways, which is what a wingbeat looks like. It multiplies
            // into the same scaleX that handles mirroring, so one transform
            // does both.
            let sx = bat.direction === 1 ? -1 : 1;
            if (flap) {
              const open = flap.min || 0.45;
              sx *= open + (1 - open) * Math.abs(Math.sin(bat.age * bat.flapRate));
            }

            node.style.transform =
              `translate3d(${bat.x}px, ${bat.y}px, 0) scale(${bat.size})`
              + (sx === 1 ? '' : ` scaleX(${sx.toFixed(3)})`);
          }
        }

        if (survivors.length !== batsRef.current.length) {
          batsRef.current = survivors;
          setBats(survivors);
        }
      }

      // ---- Mothman ----------------------------------------------------------
      if (moth_.enabled && mothmanRef.current) {
        const m = mothmanRef.current;

        m.age += dt;
        m.x += m.direction * m.speed * steps;

        // Enhanced swooping pattern
        const totalDistance = viewportWidth + 300;
        const startX = m.direction === -1 ? viewportWidth + 150 : -150;
        const progress = Math.abs(m.x - startX) / totalDistance;

        const primarySwoop = Math.sin(progress * Math.PI) * (moth_.swooping?.primaryArc || 120);
        const secondarySwoop = Math.sin(progress * Math.PI * 2) * (moth_.swooping?.secondaryFlutter || 30);
        const swoopIntensity = Math.sin(progress * Math.PI) * 0.8 + 0.2;

        m.y = m.targetY + (primarySwoop + secondarySwoop) * swoopIntensity;

        const isOffScreen = m.direction === -1 ? m.x < -200 : m.x > viewportWidth + 200;
        const isTooOld = m.age > (moth_.lifespan || 30000);

        if (isOffScreen || isTooOld) {
          mothmanRef.current = null;
          setMothman(null);
        } else if (mothmanNode.current) {
          mothmanNode.current.style.transform =
            `translate3d(${m.x}px, ${m.y}px, 0) scale(${moth_.scale || 4.0})${m.direction === 1 ? ' scaleX(-1)' : ''}`;
        }
      }

      // ---- Glowing eyes -----------------------------------------------------
      if (eyes_.enabled && eyesRef.current.length > 0) {
        const survivors = [];

        for (const eye of eyesRef.current) {
          eye.age += dt;

          // Blink chance is per reference tick, so scale by steps to keep the
          // same number of blinks per second regardless of frame rate.
          if (!eye.isBlinking && Math.random() < ((eyes_.blinkChance || 0.3) / 100) * steps) {
            eye.isBlinking = true;
            eye.blinkDuration = 300; // 300ms blink
          } else if (eye.isBlinking) {
            eye.blinkDuration -= dt;
            if (eye.blinkDuration <= 0) {
              eye.isBlinking = false;
              eye.blinkDuration = 0;
            }
          }

          // Subtle glow pulsing
          eye.blinkPhase += 0.05 * steps;

          if (eye.age > (eyes_.lifespan || 8000)) continue;

          survivors.push(eye);

          const node = eyeNodes.current.get(eye.id);
          if (node) {
            const glowPulse = Math.sin(eye.blinkPhase) * 0.3 + 0.7;
            const currentOpacity = eye.isBlinking ? 0.1 : eye.opacity * glowPulse;

            node.style.transform = `translate3d(${eye.x}px, ${eye.y}px, 0)`;
            node.style.opacity = currentOpacity;
            node.style.filter = `brightness(${glowPulse * 1.2})`;
          }
        }

        if (survivors.length !== eyesRef.current.length) {
          eyesRef.current = survivors;
          setEyes(survivors);
        }
      }

      // ---- Tomb shadows -----------------------------------------------------
      if (shadows_.enabled && tombShadowsRef.current.length > 0) {
        const survivors = [];

        for (const shadow of tombShadowsRef.current) {
          shadow.age += dt;

          shadow.x += shadow.direction * shadow.speed * steps;

          if (shadow.direction === 0) {
            // Moving up from bottom
            shadow.y -= shadow.speed * steps;
          } else {
            // Horizontal movement with slight vertical drift
            shadow.y += Math.sin(shadow.age * 0.01) * 0.5 * steps;
          }

          // Add walking phase animation
          if (shadows_.walkingEffect?.enabled) {
            shadow.walkPhase += 0.05 * steps;
            const intensity = shadows_.walkingEffect.intensity || 2;
            shadow.x += Math.sin(shadow.walkPhase) * intensity * steps;
            shadow.y += Math.cos(shadow.walkPhase * 0.7) * intensity * 0.5 * steps;
          }

          // NOTE: the flicker value below is intentionally not written to the
          // DOM. The <svg> carries a `tombShadow` CSS animation that animates
          // opacity, and CSS animations take precedence over inline styles, so
          // this value has never had a visual effect. Kept so the data model
          // matches the original.
          shadow.opacity = Math.max(
            0.05,
            Math.min(0.4, shadow.opacity + Math.sin(shadow.age * 0.02) * (shadow.flickerIntensity * 0.05))
          );

          const isOffScreen = shadow.x < -100 || shadow.x > viewportWidth + 100 ||
                              shadow.y < -100 || shadow.y > viewportHeight + 100;
          const isTooOld = shadow.age > shadow.lifespan;

          if (isOffScreen || isTooOld) continue;

          survivors.push(shadow);

          // Position goes on the wrapper - the <svg> transform belongs to the
          // tombShadow keyframes.
          const node = shadowNodes.current.get(shadow.id);
          if (node) {
            node.style.transform = `translate3d(${shadow.x}px, ${shadow.y}px, 0)`;
          }

          const ellipse = shadowEllipseNodes.current.get(shadow.id);
          if (ellipse) {
            ellipse.setAttribute('transform', `rotate(${(shadow.walkPhase * 10) % 360} 50 40)`);
          }
        }

        if (survivors.length !== tombShadowsRef.current.length) {
          tombShadowsRef.current = survivors;
          setTombShadows(survivors);
        }
      }

      // ---- Sarcophagus ------------------------------------------------------
      if (sarc_.enabled && sarcophagusRef.current) {
        const s = sarcophagusRef.current;

        s.age += dt;
        s.x += s.direction * s.speed * steps;
        s.y += (Math.abs(s.direction) < 1 ? s.direction * s.speed : 0) * steps;

        const isOffScreen = s.x < -200 || s.x > viewportWidth + 200 ||
                            s.y < -200 || s.y > viewportHeight + 200;
        const isTooOld = s.age > s.lifespan;

        if (isOffScreen || isTooOld) {
          sarcophagusRef.current = null;
          setSarcophagus(null);
        } else if (sarcophagusNode.current) {
          sarcophagusNode.current.style.transform =
            `translate3d(${s.x}px, ${s.y}px, 0) scale(${s.scale})${s.direction < 0 ? ' scaleX(-1)' : ''}`;
        }
      }

      // ---- The chase ---------------------------------------------------------
      if (chase_.enabled && chaseRef.current) {
        const c = chaseRef.current;
        c.age += dt;

        // Positions derive from age rather than accumulating per frame, so
        // the pair lands in the same place at any frame rate - the guarantee
        // this file makes everywhere else.
        const lead = c.startX + c.direction * c.speed * (c.age / TICK_MS);

        // The killer walks and still gains: the gap closes over the run.
        const progress = Math.min(1, c.age / c.lifespan);
        const gap = c.gapStart + (c.gapEnd - c.gapStart) * progress;
        const trail = lead - c.direction * gap;

        // Gait. The teen bobs fast and hard, the killer barely rises.
        const teenY = c.baseY - Math.abs(Math.sin(c.age * 0.028)) * 5;
        const slasherY = c.baseY - Math.abs(Math.sin(c.age * 0.009)) * 2;

        // Over when the TRAILING figure has left - the killer, not the teen.
        const gone = c.direction === -1 ? trail < -200 : trail > viewportWidth + 200;

        if (gone || c.age > c.lifespan) {
          chaseRef.current = null;
          setChase(null);
        } else {
          const flip = c.direction === 1 ? ' scaleX(-1)' : '';
          if (teenNode.current) {
            teenNode.current.style.transform =
              `translate3d(${lead}px, ${teenY}px, 0) scale(${c.scale * c.teenStature})${flip}`;
          }
          if (slasherNode.current) {
            slasherNode.current.style.transform =
              `translate3d(${trail}px, ${slasherY}px, 0) scale(${c.scale * c.slasherStature})${flip}`;
          }

          // Two-frame gait, chosen by age. Sprint cadence for the teen; a
          // slow, unbothered stride for the killer.
          const teenFrame = Math.floor(c.age / 110) % 2;
          const slasherFrame = Math.floor(c.age / 300) % 2;
          teenFrameNodes.current.forEach((g, i) => {
            if (g) g.style.display = i === teenFrame ? '' : 'none';
          });
          slasherFrameNodes.current.forEach((g, i) => {
            if (g) g.style.display = i === slasherFrame ? '' : 'none';
          });
        }
      }

      // ---- The walker --------------------------------------------------------
      if (walker_.enabled && walkerRef.current) {
        const w = walkerRef.current;
        w.age += dt;

        // Position from age, not accumulated per frame: same spot at any
        // frame rate. Time spent standing still is simply left out of the
        // travel, so it resumes from exactly where it stopped. No gait bob -
        // the GIF carries its own.
        const pausing = w.pauseAt !== null && w.age >= w.pauseAt && w.age < w.pauseAt + w.pauseFor;
        const travelMs = w.pauseAt === null || w.age < w.pauseAt ? w.age
          : pausing ? w.pauseAt
          : w.age - w.pauseFor;
        const x = w.startX + w.direction * w.speed * (travelMs / TICK_MS);
        const gone = w.direction === -1 ? x < -250 : x > viewportWidth + 250;

        if (gone || w.age > w.lifespan) {
          walkerRef.current = null;
          setWalker(null);
        } else if (walkerNode.current) {
          // Facing: the way it walks, except while paused - a quarter turn
          // through the diagonal on the way in and out, straight at the room
          // in between.
          let facing = w.direction === -1 ? 'west' : 'east';
          if (pausing) {
            const into = w.age - w.pauseAt;
            const left = w.pauseAt + w.pauseFor - w.age;
            facing = into < w.turn || left < w.turn
              ? (w.direction === -1 ? 'southWest' : 'southEast')
              : 'south';
          }
          if (facing !== w.facing) {
            // Swapping src restarts that GIF from its first frame, which is
            // why this happens only on an actual turn and never per frame.
            const sprite = walkerSprite(walker_.images, facing);
            w.facing = facing;
            w.flip = sprite.flip;
            if (sprite.src) walkerNode.current.src = sprite.src;
            walkerNode.current.dataset.facing = facing;
          }
          walkerNode.current.style.transform =
            `translate3d(${x}px, ${w.baseY}px, 0) scale(${w.scale})${w.flip ? ' scaleX(-1)' : ''}`;
        }
      }

      // ---- The ghosts --------------------------------------------------------
      if (ghost_.enabled && ghostsRef.current.length > 0) {
        const survivors = [];
        const amp = ghost_.bob?.amplitude ?? 16;
        const period = ghost_.bob?.period ?? 4200;
        const fadeIn = ghost_.fadeIn ?? 1500;
        const vanishOver = ghost_.vanish?.over ?? 2000;
        const peak = ghost_.opacity ?? 0.78;

        for (const g of ghostsRef.current) {
          g.age += dt;

          // Everything from age, nothing accumulated per frame.
          const x = g.startX + g.direction * g.speed * (g.age / TICK_MS);
          const y = g.baseY + Math.sin((g.age / period) * Math.PI * 2 + g.phase) * amp;
          const tilt = Math.sin((g.age / (period * 1.7)) * Math.PI * 2 + g.phase) * 5;
          // The sheet stretches and sags a little as it goes.
          const stretch = 1 + Math.sin((g.age / (period * 0.8)) * Math.PI * 2 + g.phase * 1.3) * 0.06;

          // Fade up on entry; fade out on vanishing; gone once vanished. On
          // top of that, a gutter: two slow sines at unrelated rates, so the
          // light in it never settles and never repeats within a crossing.
          const gutter = 1 - 0.16 * (0.5 + 0.5 * Math.sin(g.age * 0.031 + g.phase) * Math.sin(g.age * 0.0073 + g.phase * 2));
          let opacity = Math.min(1, g.age / fadeIn) * gutter;
          let vanished = false;
          if (g.vanishAt !== null && g.age >= g.vanishAt) {
            const left = 1 - (g.age - g.vanishAt) / vanishOver;
            opacity = Math.min(opacity, Math.max(0, left));
            vanished = left <= 0;
          }

          const gone = g.direction === -1 ? x < -260 : x > viewportWidth + 260;
          if (gone || vanished || g.age > g.lifespan) continue;

          survivors.push(g);
          const node = ghostNodes.current.get(g.id);
          if (node) {
            node.style.transform =
              `translate3d(${x}px, ${y}px, 0) rotate(${tilt}deg) scale(${g.scale}) scaleY(${stretch.toFixed(4)})${g.direction === 1 ? ' scaleX(-1)' : ''}`;
            node.style.opacity = String(opacity * peak);
          }
        }

        if (survivors.length !== ghostsRef.current.length) {
          ghostsRef.current = survivors;
          setGhosts(survivors);
        }
      }

      // ---- The cloaked man ----------------------------------------------------
      if (cloaked_.enabled && cloakedRef.current) {
        const m = cloakedRef.current;
        m.age += dt;

        // Along the floor, from age. The element is fixed to the bottom, so
        // only x and a slight tread move; the cloak's sway is CSS.
        const x = m.startX + m.direction * m.speed * (m.age / TICK_MS);
        const tread = -Math.abs(Math.sin(m.age * 0.0026)) * 4;
        const gone = m.direction === -1 ? x < -400 : x > viewportWidth + 400;

        if (gone || m.age > m.lifespan) {
          cloakedRef.current = null;
          setCloaked(null);
        } else if (cloakedNode.current) {
          cloakedNode.current.style.transform =
            `translate3d(${x}px, ${tread}px, 0)${m.direction === 1 ? ' scaleX(-1)' : ''}`;
        }
      }
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [
    theme.id,
    reducedMotion,
    batConfig.enabled,
    mothmanConfig.enabled,
    eyesConfig.enabled,
    tombShadowConfig.enabled,
    sarcophagusConfig.enabled,
    walkerConfig.enabled,
    ghostConfig.enabled,
    cloakedConfig.enabled
  ]);

  // Spawn helpers keep the ref and the React roster in lockstep.
  const spawnBat = useCallback(() => {
    if (batsRef.current.length >= MAX_BATS) return;
    const newBat = createBat();
    if (!newBat) return;
    batsRef.current = [...batsRef.current, newBat];
    setBats(batsRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id]);

  const spawnEyes = useCallback(() => {
    if (eyesRef.current.length >= (eyesConfig.maxEyes || 8)) return;
    const newEyes = createEyes();
    if (!newEyes) return;
    eyesRef.current = [...eyesRef.current, newEyes];
    setEyes(eyesRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id]);

  // Bat spawner
  useEffect(() => {
    if (!batConfig.enabled || reducedMotion) return;

    const batSpawner = setInterval(() => {
      if (Math.random() < (batConfig.spawnChance || 0.85)) {
        spawnBat();
      }
    }, Math.random() * (batConfig.spawnInterval?.max - batConfig.spawnInterval?.min || 2500) + (batConfig.spawnInterval?.min || 1500));

    return () => clearInterval(batSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, batConfig.enabled, reducedMotion, spawnBat]);

  // Mothman spawner
  useEffect(() => {
    if (!mothmanConfig.enabled || reducedMotion) return;

    const mothmanSpawner = setInterval(() => {
      if (Math.random() < (mothmanConfig.spawnChance || 0.6) && !mothmanRef.current) {
        const newMothman = createMothman();
        if (newMothman) {
          mothmanRef.current = newMothman;
          setMothman(newMothman);
        }
      }
    }, Math.random() * (mothmanConfig.spawnInterval?.max - mothmanConfig.spawnInterval?.min || 15000) + (mothmanConfig.spawnInterval?.min || 15000));

    return () => clearInterval(mothmanSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, mothman, mothmanConfig.enabled, reducedMotion]);

  // Eyes spawner - for Dark Forest theme
  useEffect(() => {
    if (!eyesConfig.enabled || reducedMotion) return;

    const eyesSpawner = setInterval(() => {
      if (Math.random() < (eyesConfig.spawnChance || 0.7)) {
        spawnEyes();
      }
    }, Math.random() * (eyesConfig.spawnInterval?.max - eyesConfig.spawnInterval?.min || 4000) + (eyesConfig.spawnInterval?.min || 2000));

    return () => clearInterval(eyesSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, eyesConfig.enabled, reducedMotion, spawnEyes]);

  // Tomb shadow spawner - for Ancient Tomb theme
  useEffect(() => {
    if (!tombShadowConfig.enabled || reducedMotion) return;

    const shadowSpawner = setInterval(() => {
      // Spawn multiple shadows at once for walking effect
      const spawnCount = Math.floor(Math.random() * (tombShadowConfig.shadowsPerSystem?.max - tombShadowConfig.shadowsPerSystem?.min + 1 || 3)) + (tombShadowConfig.shadowsPerSystem?.min || 3);

      // Limit total shadows to prevent performance issues
      const maxShadows = (tombShadowConfig.density?.max || 15) * (tombShadowConfig.shadowsPerSystem?.max || 6);
      if (tombShadowsRef.current.length >= maxShadows) return;

      const newShadows = [];
      for (let i = 0; i < spawnCount; i++) {
        const shadow = createTombShadow();
        if (shadow) {
          newShadows.push(shadow);
        }
      }

      if (newShadows.length === 0) return;

      tombShadowsRef.current = [...tombShadowsRef.current, ...newShadows];
      setTombShadows(tombShadowsRef.current);
    }, 800); // Spawn every 800ms for slow, eerie walking effect

    return () => clearInterval(shadowSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, tombShadowConfig.enabled, reducedMotion]);

  // Sarcophagus spawner - for Ancient Tomb theme
  useEffect(() => {
    if (!sarcophagusConfig.enabled || reducedMotion) return;

    const sarcophagusSpawner = setInterval(() => {
      if (Math.random() < (sarcophagusConfig.spawnChance || 0.3) && !sarcophagusRef.current) {
        const newSarcophagus = createSarcophagus();
        if (newSarcophagus) {
          sarcophagusRef.current = newSarcophagus;
          setSarcophagus(newSarcophagus);
        }
      }
    }, Math.random() * (sarcophagusConfig.spawnInterval?.max - sarcophagusConfig.spawnInterval?.min || 25000) + (sarcophagusConfig.spawnInterval?.min || 20000));

    return () => clearInterval(sarcophagusSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, sarcophagus, sarcophagusConfig.enabled, reducedMotion]);

  // Chase spawner. Same shape as the others: the interval is picked once per
  // effect run, and a new pair is only started when none is on screen.
  useEffect(() => {
    if (!chaseConfig.enabled || reducedMotion) return;

    const chaseSpawner = setInterval(() => {
      if (Math.random() < (chaseConfig.spawnChance || 0.5) && !chaseRef.current) {
        const next = createChase();
        if (next) {
          chaseRef.current = next;
          setChase(next);
        }
      }
    }, Math.random() * (chaseConfig.spawnInterval?.max - chaseConfig.spawnInterval?.min || 45000) + (chaseConfig.spawnInterval?.min || 45000));

    return () => clearInterval(chaseSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, chase, chaseConfig.enabled, reducedMotion]);

  // Walker spawner. One at a time, same cadence rules as the chase. The
  // interval is min + random spread; a spread of zero means exactly min,
  // not the `|| default` trap the older spawners fall into.
  useEffect(() => {
    if (!walkerConfig.enabled || reducedMotion) return;

    const min = walkerConfig.spawnInterval?.min ?? 30000;
    const max = walkerConfig.spawnInterval?.max ?? min;
    const walkerSpawner = setInterval(() => {
      if (Math.random() < (walkerConfig.spawnChance || 0.5) && !walkerRef.current) {
        const next = createWalker();
        if (next) {
          walkerRef.current = next;
          setWalker(next);
        }
      }
    }, min + Math.random() * Math.max(0, max - min));

    return () => clearInterval(walkerSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, walker, walkerConfig.enabled, reducedMotion]);

  // Ghost spawner. A few at a time, up to `max`; the interval is min plus a
  // random spread, and a zero spread means exactly min.
  useEffect(() => {
    if (!ghostConfig.enabled || reducedMotion) return;

    const min = ghostConfig.spawnInterval?.min ?? 5000;
    const max = ghostConfig.spawnInterval?.max ?? min;
    const ghostSpawner = setInterval(() => {
      if (ghostsRef.current.length >= (ghostConfig.max ?? 3)) return;
      if (Math.random() < (ghostConfig.spawnChance ?? 0.7)) {
        const next = createGhost();
        if (next) {
          ghostsRef.current = [...ghostsRef.current, next];
          setGhosts(ghostsRef.current);
        }
      }
    }, min + Math.random() * Math.max(0, max - min));

    return () => clearInterval(ghostSpawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, ghostConfig.enabled, reducedMotion]);

  // Cloaked man spawner. One at a time, from time to time.
  useEffect(() => {
    if (!cloakedConfig.enabled || reducedMotion) return;

    const min = cloakedConfig.spawnInterval?.min ?? 40000;
    const max = cloakedConfig.spawnInterval?.max ?? min;
    const spawner = setInterval(() => {
      if (Math.random() < (cloakedConfig.spawnChance ?? 0.6) && !cloakedRef.current) {
        const next = createCloaked();
        if (next) {
          cloakedRef.current = next;
          setCloaked(next);
        }
      }
    }, min + Math.random() * Math.max(0, max - min));

    return () => clearInterval(spawner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, cloaked, cloakedConfig.enabled, reducedMotion]);

  // The secret door's opener. A one-shot timer that re-arms itself, like
  // the lightning's: the door stands open for a random while; for the last
  // `retreat` of that the hand withdraws and the eyes go out; then it shuts
  // and waits again. Off under reduced motion, with the rest of the room.
  useEffect(() => {
    if (!doorConfig.enabled || reducedMotion) return undefined;
    const min = doorConfig.interval?.min ?? 25000;
    const max = doorConfig.interval?.max ?? min;
    const openMin = doorConfig.openFor?.min ?? 6000;
    const openMax = doorConfig.openFor?.max ?? openMin;
    const retreat = doorConfig.retreat ?? 1600;
    let timer = null;
    const wait = () => {
      timer = setTimeout(() => {
        setDoorPhase('open');
        const openFor = openMin + Math.random() * Math.max(0, openMax - openMin);
        timer = setTimeout(() => {
          setDoorPhase('closing');
          timer = setTimeout(() => {
            setDoorPhase('shut');
            wait();
          }, Math.min(retreat, openFor));
        }, Math.max(0, openFor - retreat));
      }, min + Math.random() * Math.max(0, max - min));
    };
    wait();
    return () => { clearTimeout(timer); setDoorPhase('shut'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, doorConfig.enabled, reducedMotion]);

  // Lightning scheduler. A one-shot timer that re-arms with a fresh random
  // interval, like the jump scare's; off under reduced motion, since a
  // flash is the one thing here that could genuinely bother someone.
  useEffect(() => {
    if (!windowsConfig.enabled || reducedMotion) return undefined;

    const min = windowsConfig.lightning?.interval?.min ?? 9000;
    const max = windowsConfig.lightning?.interval?.max ?? 24000;
    let timer = null;
    const schedule = () => {
      timer = setTimeout(() => {
        setLightning(nextCreatureId());
        schedule();
      }, min + Math.random() * Math.max(0, max - min));
    };
    schedule();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, windowsConfig.enabled, reducedMotion]);

  // A strike is over when its flash elements are removed, after `hold`.
  useEffect(() => {
    if (lightning === null) return undefined;
    const t = setTimeout(() => setLightning(null), windowsConfig.lightning?.hold ?? 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightning]);

  // The face at the window. Same shape as the lightning: a one-shot timer
  // that re-arms itself; a visit is a short-lived id whose element carries
  // the whole lean-in, look, and withdrawal in CSS, and is removed after
  // `hold`. Off under reduced motion.
  const peekerConfig = windowsConfig.peeker || {};
  const [peek, setPeek] = useState(null);
  useEffect(() => {
    if (!windowsConfig.enabled || !peekerConfig.enabled || reducedMotion) return undefined;
    const min = peekerConfig.interval?.min ?? 20000;
    const max = peekerConfig.interval?.max ?? min;
    let timer = null;
    const schedule = () => {
      timer = setTimeout(() => {
        setPeek(nextCreatureId());
        schedule();
      }, min + Math.random() * Math.max(0, max - min));
    };
    schedule();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, windowsConfig.enabled, peekerConfig.enabled, reducedMotion]);
  useEffect(() => {
    if (peek === null) return undefined;
    const t = setTimeout(() => setPeek(null), peekerConfig.hold ?? 3400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peek]);

  // Jump scare scheduler. Never on arrival - firstDelay lets people settle -
  // then a one-shot timer that re-arms itself with a fresh random interval,
  // firing with spawnChance each time. Reduced motion switches it off at the
  // source rather than merely hiding it.
  useEffect(() => {
    if (!jumpScareConfig.enabled || reducedMotion) return undefined;

    let timer = null;
    const schedule = (delay) => {
      timer = setTimeout(() => {
        if (Math.random() < (jumpScareConfig.spawnChance ?? 0.6)) {
          setScare({
            id: nextCreatureId(),
            tilt: (Math.random() - 0.5) * 10,            // a few degrees off plumb
            offsetX: (Math.random() - 0.5) * 12          // and a little off centre
          });
        }
        const min = jumpScareConfig.spawnInterval?.min ?? 180000;
        const max = jumpScareConfig.spawnInterval?.max ?? 360000;
        schedule(min + Math.random() * (max - min));
      }, delay);
    };

    schedule(jumpScareConfig.firstDelay ?? 90000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme.id, jumpScareConfig.enabled, reducedMotion]);

  // The scare ends by removal, not by fading: a hard cut-out is the point.
  // One element, one mount, one unmount - there is deliberately nothing here
  // that could toggle it, which is what keeps this on the right side of the
  // three-flashes-a-second line.
  useEffect(() => {
    if (!scare) return undefined;
    const t = setTimeout(() => setScare(null), jumpScareConfig.hold ?? 450);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scare]);

  return (
    <>
      {/* Background Gradient - Behind everything */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: theme.background?.gradient || 'radial-gradient(ellipse at center, #1a1a2e 0%, #16213e 35%, #0f0f23 100%)',
          zIndex: -30
        }}
      />

      {/* The lot. Fixed to the viewport rather than absolute, so the cars stay
          on the horizon instead of sliding away down a long page. */}
      {parkedCars.map((car) => (
        <div
          key={car.id}
          className="pointer-events-none"
          style={{
            position: 'fixed',
            left: `${car.leftPct}%`,
            bottom: `${car.bottom}px`,
            width: `${car.width}px`,
            transform: 'translateX(-50%)',
            opacity: car.opacity,
            zIndex: car.zIndex
          }}
        >
          <svg viewBox="0 0 100 62" width="100%" style={{ display: 'block' }}>
            {/* Seen from behind: boot, then cabin, then roof */}
            <path
              d="M6,60 L6,32 Q6,27 11,26 L23,26 L29,10 Q30,6 35,6 L65,6 Q70,6 71,10 L77,26 L89,26
                 Q94,27 94,32 L94,60 Z"
              fill={car.color}
            />
            {/* A thin lit edge along the roof and boot. Without it the body is
                one flat shape and the car only reads by its lights. */}
            <path
              d="M6,32 Q6,27 11,26 L23,26 L29,10 Q30,6 35,6 L65,6 Q70,6 71,10 L77,26 L89,26 Q94,27 94,32"
              fill="none"
              stroke="rgba(180,198,240,0.16)"
              strokeWidth="1.2"
            />
            {/* Rear window, darker than the body so the cabin reads as glass */}
            <path d="M33,24 L37,10 Q37.5,8 40,8 L60,8 Q62.5,8 63,10 L67,24 Z" fill="#080a12" opacity="0.9" />
            {/* Tail lights */}
            {[24, 76].map((cx, i) => {
              const lit = i === 0 ? car.litLeft : car.litRight;
              return (
                <ellipse
                  key={cx}
                  cx={cx}
                  cy={41}
                  rx="5"
                  ry="2.8"
                  fill={lit ? '#e0362b' : '#3d1210'}
                  style={lit ? { filter: `drop-shadow(0 0 ${car.glow}px rgba(255,70,58,0.85))` } : undefined}
                />
              );
            })}
          </svg>
        </div>
      ))}

      {/* The treeline. Fixed to the viewport, like the lot, so the wood stays
          on the horizon rather than sliding away down a long page. */}
      {treeline.map((tree) => (
        <div
          key={`tree-${tree.id}`}
          data-tree={tree.id}
          className="pointer-events-none"
          style={{
            position: 'fixed',
            left: `${tree.leftPct}%`,
            bottom: '-6px',
            width: `${tree.width}px`,
            height: `${tree.height}px`,
            transform: `translateX(-50%) rotate(${tree.lean}deg)`,
            transformOrigin: 'bottom center',
            opacity: tree.opacity,
            zIndex: tree.zIndex
          }}
        >
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            width="100%"
            height="100%"
            style={{ display: 'block' }}
          >
            <path d={coniferPath(tree.notch)} fill={tree.color} />
          </svg>
        </div>
      ))}

      {/* The house's windows: a storm beyond the glass. Fixed and behind the
          interface; the frame and the rain are CSS, the lightning is a flash
          element that exists only for the strike's duration. */}
      {windowsConfig.enabled && (windowsConfig.positions || []).map((pos, i) => (
        <div
          key={`window-${i}`}
          data-window={i}
          className="hh-window pointer-events-none"
          style={{
            position: 'fixed',
            top: `${(pos.top ?? 0.1) * 100}%`,
            ...(pos.right !== undefined
              ? { right: `${pos.right * 100}%` }
              : { left: `${(pos.left ?? 0.05) * 100}%` }),
            zIndex: -18
          }}
        >
          <div className="hh-pane">
            <div className="hh-sky" />
            {/* The face at the glass: between the sky and the rain, so it is
                outside, in the weather, behind the bars. Leans in from the
                right edge on a tilt, looks, withdraws - all in CSS; the
                element exists only for the visit. */}
            {peek !== null && i === (peekerConfig.window ?? 0) && (
              <div
                key={`peek-${peek}`}
                data-peeker
                className="hh-peeker"
                style={{ animationDuration: `${peekerConfig.hold ?? 3400}ms` }}
              >
                <svg viewBox="0 0 100 112" width="100%" style={{ display: 'block', overflow: 'visible' }}>
                  {/* The body it belongs to: a cloaked figure leaning in from
                      the right, shoulders below the head, the cloak running
                      off down and to the right beyond the pane (which clips
                      it), and one clawed hand pressed flat to the glass. */}
                  <path data-body d="M8,200 L8,118 Q14,104 28,96 Q50,110 72,94 Q86,96 96,108 L150,170 L150,200 Z" fill="#0a0608" />
                  <path d="M28,96 Q50,110 72,94 L74,100 Q50,116 26,102 Z" fill="#1a1016" />
                  <path d="M22,100 Q10,92 4,76 L10,74 Q16,88 28,94 Z" fill="#0a0608" />
                  <path d="M78,96 Q92,88 98,72 L104,76 Q98,92 84,102 Z" fill="#0a0608" />
                  {/* A hand pressed flat to the glass: palm low, four fingers
                      pointing up with a modest spread, the thumb out to the
                      side. Angles: -90 is straight up. */}
                  <g data-hand>
                    <ellipse cx="18" cy="92" rx="7.5" ry="9.5" fill="#c9cfc4" transform="rotate(-8 18 92)" />
                    {[[-118, 19], [-100, 22], [-82, 21], [-64, 17], [-165, 12]].map(([ang, len]) => {
                      const a = (ang * Math.PI) / 180;
                      const ux = Math.cos(a); const uy = Math.sin(a);
                      const bx = 18 + ux * 5; const by = 92 + uy * 6;
                      const tx = 18 + ux * len; const ty = 92 + uy * len;
                      return (
                        <g key={ang}>
                          <path d={`M${bx.toFixed(1)},${by.toFixed(1)} L${tx.toFixed(1)},${ty.toFixed(1)}`} stroke="#c9cfc4" strokeWidth="3.4" strokeLinecap="round" fill="none" />
                          <path d={`M${(tx - uy).toFixed(1)},${(ty + ux).toFixed(1)} L${(tx + ux * 4.5).toFixed(1)},${(ty + uy * 4.5).toFixed(1)} L${(tx + uy).toFixed(1)},${(ty - ux).toFixed(1)} Z`} fill="#e8e4d8" />
                        </g>
                      );
                    })}
                  </g>
                  {/* Collar, then the head: a long pale skull, bat's ears */}
                  <path d="M18,112 L30,92 Q50,104 70,92 L82,112 Z" fill="#0a0608" />
                  <path data-ear d="M22,40 L2,6 L31,24 Z" fill="#c9cfc4" />
                  <path d="M20,37 L9,16 L26,28 Z" fill="#7a5a68" />
                  <path data-ear d="M78,40 L98,6 L69,24 Z" fill="#b9c0b4" />
                  <path d="M80,37 L91,16 L74,28 Z" fill="#6a4a58" />
                  <path data-head d="M50,12 C28,12 18,32 20,56 C22,78 34,96 50,100 C66,96 78,78 80,56 C82,32 72,12 50,12 Z" fill="#c9cfc4" />
                  <path d="M50,12 C28,12 18,32 20,56 C21,70 27,84 36,93 C30,80 26,66 27,52 C29,30 38,16 50,12 Z" fill="#aab2a8" opacity="0.7" />
                  {/* Hollow cheeks, a heavy brow */}
                  <ellipse cx="31" cy="70" rx="6" ry="9" fill="#8f978f" opacity="0.55" />
                  <ellipse cx="69" cy="70" rx="6" ry="9" fill="#8f978f" opacity="0.55" />
                  <path d="M26,49 Q50,36 74,49 Q50,45 26,49 Z" fill="#8a938a" />
                  {/* The eyes: yellow, lit, slit pupils, lids drawn down at the inner corners */}
                  <g data-eyes style={{ filter: 'drop-shadow(0 0 5px rgba(255, 210, 30, 0.85))' }}>
                    <ellipse data-eye-yellow cx="38" cy="55" rx="7" ry="5" fill="#ffd21f" />
                    <ellipse data-eye-yellow cx="62" cy="55" rx="7" ry="5" fill="#ffd21f" />
                  </g>
                  <ellipse cx="38.5" cy="55" rx="1.4" ry="4.2" fill="#1a1000" />
                  <ellipse cx="61.5" cy="55" rx="1.4" ry="4.2" fill="#1a1000" />
                  <path d="M30,52 Q38,47 46,52 L46,55 Q38,52 30,55 Z" fill="#4a524a" opacity="0.9" />
                  <path d="M70,52 Q62,47 54,52 L54,55 Q62,52 70,55 Z" fill="#4a524a" opacity="0.9" />
                  {/* A snub nose, two nostrils */}
                  <path d="M50,57 L45,67 L55,67 Z" fill="#b7bdb5" />
                  <ellipse cx="46.5" cy="67" rx="2.4" ry="1.5" fill="#3a2f3a" />
                  <ellipse cx="53.5" cy="67" rx="2.4" ry="1.5" fill="#3a2f3a" />
                  {/* The snarl: lips drawn back off the teeth, the corners
                      pulled down, creases at each, and the fangs long */}
                  <path data-mouth d="M31,81 Q50,75 69,81 Q50,87 31,81 Z" fill="#2a0f14" />
                  <path d="M31,81 Q50,75 69,81" fill="none" stroke="#5a2a30" strokeWidth="1.2" />
                  <path d="M31,81 L27,86" stroke="#5a2a30" strokeWidth="1.2" strokeLinecap="round" />
                  <path d="M69,81 L73,86" stroke="#5a2a30" strokeWidth="1.2" strokeLinecap="round" />
                  <path d="M36,80 L64,80" stroke="#e8e4d8" strokeWidth="1.6" opacity="0.8" />
                  <path data-fang d="M38,79.5 L41.5,95 L45,80.5 Z" fill="#f2f0e8" />
                  <path data-fang d="M55,80.5 L58.5,95 L62,79.5 Z" fill="#f2f0e8" />
                </svg>
              </div>
            )}
            <div className="hh-rain hh-rain--far" />
            <div className="hh-rain hh-rain--near" />
            {lightning !== null && <div key={`bolt-${lightning}`} data-lightning className="hh-bolt" />}
            <div className="hh-mullions" />
          </div>
          <div className="hh-sill" />
        </div>
      ))}

      {/* The room lit by the strike, faintly, for as long as the glass is. */}
      {windowsConfig.enabled && lightning !== null && (
        <div key={`roomflash-${lightning}`} data-roomflash className="hh-roomflash pointer-events-none" />
      )}

      {/* The fireplace: stone surround, a mantel, logs, and flames that are
          three tongues each in three colours, every one on its own flicker.
          Its light is a separate glow laid up the wall and across the floor. */}
      {fireplaceConfig.enabled && (
        <>
          <div
            data-fireglow
            className="hh-fireglow pointer-events-none"
            style={{ [fireplaceConfig.side === 'right' ? 'right' : 'left']: 0, zIndex: -19 }}
          />
          <div
            data-fireplace
            className="hh-fireplace pointer-events-none"
            style={{
              position: 'fixed',
              bottom: 0,
              [fireplaceConfig.side === 'right' ? 'right' : 'left']: '2vw',
              width: FIREPLACE_WIDTH,
              zIndex: -17
            }}
          >
            <svg viewBox="0 0 200 150" width="100%" style={{ display: 'block' }}>
              {/* Surround and mantel */}
              <rect x="0" y="18" width="200" height="132" fill="#2b1d12" />
              <rect x="0" y="12" width="200" height="12" rx="2" fill="#3d2a19" />
              <rect x="4" y="22" width="192" height="6" fill="#1a110a" opacity="0.6" />
              {/* Stones, offset by row like brickwork */}
              {[0, 1, 2, 3, 4].map((row) => (
                [0, 1, 2, 3, 4, 5].map((col) => {
                  const w = 34; const h = 24; const x = col * w - (row % 2) * 17; const y = 30 + row * h;
                  const inFirebox = x + w > 40 && x < 160 && y + h > 48;
                  return inFirebox ? null : (
                    <rect key={`${row}-${col}`} x={x + 1} y={y + 1} width={w - 2} height={h - 2} rx="2"
                      fill={(row + col) % 3 === 0 ? '#4a3524' : (row + col) % 3 === 1 ? '#3f2d1e' : '#44311f'} />
                  );
                })
              ))}
              {/* The firebox */}
              <path d="M40,150 L40,70 Q40,48 60,48 L140,48 Q160,48 160,70 L160,150 Z" fill="#070402" />
              <path d="M44,150 L44,72 Q44,52 62,52 L138,52 Q156,52 156,72 L156,150 Z" fill="#120904" />
              {/* Embers on the hearth floor */}
              <ellipse cx="100" cy="138" rx="48" ry="6" fill="#ff7a1a" opacity="0.35" className="hh-ember" />
              {/* Logs */}
              <rect data-log x="62" y="120" width="76" height="12" rx="6" fill="#3a2414" transform="rotate(-8 100 126)" />
              <rect data-log x="60" y="126" width="80" height="12" rx="6" fill="#4a2f1a" transform="rotate(7 100 132)" />
              {/* Flames: three tongues, each three layers deep, all alight */}
              {[[100, 128, 1.0, 0], [78, 130, 0.72, 1], [122, 130, 0.64, 2]].map(([x, y, s, i]) => (
                <g key={`tongue-${i}`} transform={`translate(${x} ${y}) scale(${s})`}>
                  {[['#ff6a00', 1.0, 0.95], ['#ffb400', 0.72, 0.95], ['#fff4c2', 0.42, 0.9]].map(([fill, k, op], j) => (
                    <g
                      key={j}
                      data-flame
                      className="hh-flame"
                      style={{ animationDuration: `${0.55 + i * 0.12 + j * 0.08}s`, animationDelay: `${-(i * 0.3 + j * 0.17)}s` }}
                    >
                      <path
                        d="M0,0 C-20,-18 -24,-44 -6,-62 C-2,-72 2,-72 6,-62 C24,-44 20,-18 0,0 Z"
                        fill={fill}
                        opacity={op}
                        transform={`scale(${k})`}
                      />
                    </g>
                  ))}
                </g>
              ))}
            </svg>
          </div>
        </>
      )}

      {/* The candles. Each hangs at its configured spot and bobs on its own
          CSS animation; the flame flickers on another. */}
      {candles.map((c) => (
        <div
          key={`candle-${c.id}`}
          data-candle={c.id}
          className="hh-candle pointer-events-none"
          style={{
            position: 'fixed',
            left: `${c.leftPct}%`,
            top: `${c.topPct}%`,
            width: `${22 * c.size}px`,
            zIndex: -16,
            animationDuration: `${c.duration}s`,
            animationDelay: `${c.delay}s`
          }}
        >
          <div className="hh-candle-glow" style={{ animationDuration: `${c.flicker * 2.6}s` }} />
          <svg viewBox="0 0 40 110" width="100%" style={{ display: 'block', overflow: 'visible' }}>
            <g data-flame className="hh-flame" style={{ animationDuration: `${c.flicker}s`, animationDelay: `${c.delay}s` }}>
              <path d="M20,34 C12,26 10,16 20,2 C30,16 28,26 20,34 Z" fill="#ffb400" opacity="0.95" />
              <path d="M20,32 C16,27 15,20 20,12 C25,20 24,27 20,32 Z" fill="#fff4c2" opacity="0.9" />
            </g>
            <rect x="19" y="30" width="2" height="8" fill="#1a110a" />
            <path d="M8,38 L32,38 L31,104 Q20,110 9,104 Z" fill="#f1e6c8" />
            <path d="M8,38 Q20,44 32,38 L32,46 Q24,50 20,58 Q16,50 8,46 Z" fill="#fbf4e0" />
            <path d="M12,46 Q10,60 13,72 Q15,60 12,46 Z" fill="#fbf4e0" opacity="0.9" />
          </svg>
        </div>
      ))}

      {/* The bookcase: five shelves against the far wall. Books laid out from
          the index, so the room is the same room every visit. */}
      {bookshelvesConfig.enabled && (
        <div
          data-bookshelf
          className="hh-bookshelf pointer-events-none"
          style={{
            position: 'fixed',
            bottom: 0,
            [bookshelvesConfig.side === 'left' ? 'left' : 'right']: '2vw',
            zIndex: -17
          }}
        >
          <svg viewBox="0 0 200 260" width="100%" style={{ display: 'block' }}>
            {/* Carcass, back panel, and the shelves themselves */}
            <rect x="0" y="0" width="200" height="260" rx="3" fill="#2b1d12" />
            <rect x="6" y="6" width="188" height="248" fill="#140c07" />
            <rect x="0" y="0" width="200" height="8" fill="#3d2a19" />
            {Array.from({ length: bookshelvesConfig.shelves || 5 }, (_, s) => {
              const shelfY = 6 + (s + 1) * (248 / (bookshelvesConfig.shelves || 5));
              return (
                <g key={`shelf-${s}`} data-shelf={s}>
                  {books.filter((b) => b.shelf === s).map((b, i) => (
                    <rect
                      key={i}
                      data-book
                      x={4 + b.x}
                      y={shelfY - 4 - b.h}
                      width={b.w}
                      height={b.h}
                      rx="1"
                      fill={b.colour}
                      transform={b.lean ? `rotate(${b.lean} ${4 + b.x + b.w} ${shelfY - 4})` : undefined}
                    />
                  ))}
                  <rect x="4" y={shelfY - 4} width="192" height="5" fill="#3d2a19" />
                  <rect x="4" y={shelfY + 1} width="192" height="2" fill="#0d0805" opacity="0.7" />
                </g>
              );
            })}
            <rect x="0" y="0" width="6" height="260" fill="#3d2a19" />
            <rect x="194" y="0" width="6" height="260" fill="#3d2a19" />
          </svg>
        </div>
      )}

      {/* The portrait over the mantel: an old man with severe features, in
          a gilt frame, lit from below by the fire. Placed from the fireplace's
          own width, so it hangs over the hearth at every viewport size. */}
      {portraitConfig.enabled && fireplaceConfig.enabled && (
        <div
          data-portrait
          data-over="fireplace"
          className="hh-portrait pointer-events-none"
          style={{
            position: 'fixed',
            bottom: `calc(${FIREPLACE_WIDTH} * 0.75 + 16px)`,
            [fireplaceConfig.side === 'right' ? 'right' : 'left']: `calc(2vw + ${FIREPLACE_WIDTH} * 0.27)`,
            width: `calc(${FIREPLACE_WIDTH} * 0.46)`,
            zIndex: -17
          }}
        >
          <svg ref={portraitSvg} viewBox="0 0 120 150" width="100%" style={{ display: 'block' }}>
            <defs>
              <radialGradient id="hh-portrait-vignette" cx="50%" cy="45%" r="60%">
                <stop offset="0.45" stopColor="#000" stopOpacity="0" />
                <stop offset="1" stopColor="#000" stopOpacity="0.75" />
              </radialGradient>
              <linearGradient id="hh-portrait-firelight" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0" stopColor="#ffb060" stopOpacity="0.22" />
                <stop offset="0.6" stopColor="#ffb060" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* The frame: gilt, worn at the edges, with a bevel */}
            <rect data-frame x="0" y="0" width="120" height="150" rx="2" fill="#6b5120" />
            <rect x="3" y="3" width="114" height="144" fill="none" stroke="#8f6d2a" strokeWidth="2" />
            <rect x="8" y="8" width="104" height="134" fill="none" stroke="#3f2d10" strokeWidth="2" />
            {[[6, 6], [114, 6], [6, 144], [114, 144]].map(([cx, cy]) => (
              <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3" fill="#a07f34" />
            ))}
            {/* The canvas, dark as a cellar */}
            <rect x="11" y="11" width="98" height="128" fill="#15100a" />
            <rect x="11" y="11" width="98" height="128" fill="#2a1d10" opacity="0.5" />
            {/* The sitter. Coat, high collar, cravat */}
            <path d="M22,139 Q30,104 60,98 Q90,104 98,139 Z" fill="#0a0806" />
            <path d="M44,104 L60,98 L76,104 L72,112 L60,106 L48,112 Z" fill="#141010" />
            <path d="M52,100 L68,100 L65,118 L55,118 Z" fill="#d8d0c0" />
            <rect x="52" y="82" width="16" height="20" fill="#b89a7e" />
            {/* The face: long, gaunt, the jaw sharp */}
            <path data-face d="M40,52 Q40,22 60,20 Q80,22 80,52 Q81,74 70,86 Q60,92 50,86 Q39,74 40,52 Z" fill="#cdb79c" />
            {/* Hollow cheeks and the shadow under the brow */}
            <ellipse cx="47" cy="66" rx="5.5" ry="10" fill="#7a5a44" opacity="0.45" />
            <ellipse cx="73" cy="66" rx="5.5" ry="10" fill="#7a5a44" opacity="0.45" />
            <path d="M43,46 Q60,40 77,46 Q60,53 43,46 Z" fill="#5a4030" opacity="0.6" />
            {/* Hair: white, thin, gone from the crown; sideburns long */}
            <path d="M40,50 Q35,36 42,26 Q48,20 58,22 Q47,28 44,50 Z" fill="#d9d4c8" />
            <path d="M80,50 Q85,36 78,26 Q72,20 62,22 Q73,28 76,50 Z" fill="#d9d4c8" />
            <path d="M40,52 L38,74 L44,72 L44,54 Z" fill="#cfc9bc" />
            <path d="M80,52 L82,74 L76,72 L76,54 Z" fill="#cfc9bc" />
            {/* Brows, heavy and drawn down toward the nose */}
            <path d="M44,45 L56,48" stroke="#3a2a20" strokeWidth="2.2" strokeLinecap="round" />
            <path d="M76,45 L64,48" stroke="#3a2a20" strokeWidth="2.2" strokeLinecap="round" />
            {/* Eyes: sunk in their sockets, a point of light in each. The
                pupils sit in a group per eye that the gaze effect moves, so
                they follow whoever is in the room; the sockets stay put. */}
            <ellipse cx="52.5" cy="52.5" rx="5.5" ry="4" fill="#2a1a12" />
            <ellipse cx="70.5" cy="52.5" rx="5.5" ry="4" fill="#2a1a12" />
            {[52.5, 70.5].map((cx, i) => (
              <g key={cx} data-gaze={i} ref={(el) => { gazeNodes.current[i] = el; }}>
                <ellipse data-eye cx={cx} cy="52.5" rx="2" ry="1.6" fill="#e8e0d0" />
                <circle cx={cx + 0.7} cy="52.6" r="0.9" fill="#050404" />
              </g>
            ))}
            {/* A long nose, and a mouth that has not smiled in years */}
            <path d="M60,50 L55,68 L65,68 Z" fill="#b0906f" />
            <path d="M60,50 L55,68" stroke="#7a5a44" strokeWidth="1" opacity="0.7" />
            <path d="M50,78 Q60,72.5 70,78" fill="none" stroke="#4a2a22" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M48,80 Q60,74 72,80" fill="none" stroke="#2a1a12" strokeWidth="0.8" opacity="0.6" />
            {/* The fire's light from below, and the dark closing in at the edges */}
            <rect x="11" y="11" width="98" height="128" fill="url(#hh-portrait-firelight)" />
            <rect x="11" y="11" width="98" height="128" fill="url(#hh-portrait-vignette)" />
          </svg>
        </div>
      )}

      {/* The further portraits, to the old man's right at differing heights.
          Hung from the viewport and sized from the fireplace, like him. */}
      {portraitsConfig.enabled && (portraitsConfig.items || []).map((p, i) => (
        PORTRAIT_SITTERS[p.kind] ? (
          <div
            key={`portrait-${p.kind}-${i}`}
            data-portrait-extra={p.kind}
            className="hh-portrait pointer-events-none"
            style={{
              position: 'fixed',
              left: `${(p.left ?? 0.4) * 100}%`,
              bottom: `${(p.bottom ?? 0.3) * 100}%`,
              width: `calc(${FIREPLACE_WIDTH} * ${p.width ?? 0.4})`,
              zIndex: -17
            }}
          >
            <GiltFrame
              id={`hh-portrait-${p.kind}`}
              light={p.kind === 'knight' ? 0.08 : 0.12}
              svgRef={p.kind === 'woman' ? womanSvg : undefined}
            >
              {PORTRAIT_SITTERS[p.kind]({
                gazeNode: p.kind === 'woman' ? (i) => (el) => { womanGazeNodes.current[i] = el; } : undefined
              })}
            </GiltFrame>
          </div>
        ) : null
      ))}

      {/* The secret door. Shut, it is a panel of wall with seams a little too
          regular. Open, the panel swings back on CSS, the void behind it
          shows, two eyes light, and a hand takes the jamb. Rendered through
          a portal as a child of <body>, like the jump scare, so that when it
          comes to the front nothing in the app tree can keep it behind. */}
      {doorConfig.enabled && typeof document !== 'undefined' && createPortal(
        <div
          data-door
          data-open={doorOpen ? 1 : 0}
          data-phase={doorPhase}
          data-layer={doorLayer}
          className={`hh-door pointer-events-none${doorOpen ? ' hh-door--open' : ''}${doorPhase === 'closing' ? ' hh-door--closing' : ''}${doorSinking ? ' hh-door--sinking' : ''}`}
          style={{
            position: 'fixed',
            bottom: 0,
            left: `${(doorConfig.left ?? 0.55) * 100}%`,
            zIndex: doorLayer === 'front' ? 75 : -17
          }}
        >
          <div className="hh-door-void">
            <span data-eye-red className="hh-door-eye hh-door-eye--l" />
            <span data-eye-red className="hh-door-eye hh-door-eye--r" />
          </div>
          <div className="hh-door-frame" />
          <div className="hh-door-panel" />
          {/* The reach comes LAST, after the frame and the panel, so the hand
              paints over the jamb it grips. Nothing is clipped: the arm has
              no body and comes from beside the jamb, so there is nothing that
              could show on the wrong side of the door. */}
          <div className="hh-door-reach">
            <svg data-claw className="hh-door-claw" viewBox="0 0 130 210" style={{ display: 'block', overflow: 'visible' }}>
              <defs>
                {/* The forearm fades to nothing toward where it comes from,
                    so it reads as emerging from the dark rather than ending. */}
                <linearGradient id="hh-arm-fade" gradientUnits="userSpaceOnUse" x1="104" y1="78" x2="84" y2="128">
                  <stop offset="0" stopColor="#1f2c27" stopOpacity="1" />
                  <stop offset="0.55" stopColor="#1f2c27" stopOpacity="0.7" />
                  <stop offset="1" stopColor="#1f2c27" stopOpacity="0" />
                </linearGradient>
              </defs>
              {/* This box is the door's own height (210 units for a door 2.1
                  times its width) and 130% of its width, so one unit is 1% of
                  the door's width, the jamb is at x=100, and the eyes, 38%
                  of the way down, are at y≈80. The hand is pressed flat
                  against the wall beside the door, a little above eye level,
                  fingers splayed; the forearm comes back round the jamb into
                  the dark, where it fades out. No body: whatever it belongs
                  to stays in there. */}
              <path data-arm d="M104,78 C98,88 92,104 84,124 L95,128 C100,112 105,98 112,88 Z" fill="url(#hh-arm-fade)" />
              <path d="M106,82 C100,92 95,106 89,120" fill="none" stroke="#2f4038" strokeWidth="1" opacity="0.5" />
              {/* The palm, flat on the wall */}
              <ellipse data-palm cx="114" cy="72" rx="8.5" ry="10.5" fill="#223029" stroke="#121a16" strokeWidth="0.8" transform="rotate(-12 114 72)" />
              {/* Four fingers and a thumb, fanned out from the palm, each
                  drawn as a dark-rimmed stroke with a bone talon at the tip
                  continuing its line. Angles: 0 is right, negative is up. */}
              {/* The forearm leaves the palm toward the lower left (about
                  125 degrees), so the thumb fans out the other way, up and
                  to the left, outside the fingers - not back down the arm. */}
              {[
                { angle: -100, reach: 21, thumb: false },
                { angle: -64, reach: 23, thumb: false },
                { angle: -26, reach: 22, thumb: false },
                { angle: 12, reach: 19, thumb: false },
                { angle: -150, reach: 15, thumb: true }
              ].map(({ angle, reach, thumb }) => {
                const a = (angle * Math.PI) / 180;
                const ux = Math.cos(a); const uy = Math.sin(a);
                const base = [114 + ux * 6, 72 + uy * 7];
                const tip = [114 + ux * reach, 72 + uy * reach];
                const claw = [114 + ux * (reach + 5), 72 + uy * (reach + 5)];
                const px = -uy * 1.5; const py = ux * 1.5;          // across the finger
                const d = `M${base[0].toFixed(1)},${base[1].toFixed(1)} L${tip[0].toFixed(1)},${tip[1].toFixed(1)}`;
                return (
                  <g key={angle} data-angle={angle} {...(thumb ? { 'data-claw-thumb': true } : { 'data-claw-finger': true })}>
                    <path d={d} fill="none" stroke="#121a16" strokeWidth={thumb ? 5.6 : 5.4} strokeLinecap="round" />
                    <path data-finger-shape d={d} fill="none" stroke={thumb ? '#243229' : '#283a32'} strokeWidth={thumb ? 4.2 : 4} strokeLinecap="round" />
                    <circle cx={base[0] + ux * 2} cy={base[1] + uy * 2} r="1.6" fill="#3a5046" opacity="0.8" />
                    <path
                      data-talon
                      d={`M${(tip[0] + px).toFixed(1)},${(tip[1] + py).toFixed(1)} L${claw[0].toFixed(1)},${claw[1].toFixed(1)} L${(tip[0] - px).toFixed(1)},${(tip[1] - py).toFixed(1)} Z`}
                      fill="#d9d4c8"
                    />
                  </g>
                );
              })}
            </svg>
          </div>
        </div>,
        document.body
      )}

      {/* The raven. Where it perches is in App.css - the bookcase's top on a
          wide screen, the mantel on a phone - so that it follows the same
          breakpoints that hide the bookcase. Facing left; black on black
          wood, so the edge is picked out by a cold rim and the eye by a
          glint. The head turns now and then on a CSS animation. */}
      {ravenConfig.enabled && (
        <div data-raven className="hh-raven pointer-events-none">
          {/* Upright, chest to the room, head turned three-quarters to the
              viewer so both eyes show and the beak comes forward and down.
              The box is taller than it is wide, and the toes are drawn on
              its very bottom edge, so wherever the box is set down the
              bird is standing on it. */}
          <svg viewBox="0 0 100 130" width="100%" style={{ display: 'block', overflow: 'visible' }}>
            {/* Tail, back and to the right, down behind the perch's edge */}
            <path data-tail d="M60,98 L92,122 L88,104 L68,90 Z" fill="#0b0b10" stroke="#2a2f3a" strokeWidth="0.8" />
            {/* The body: an upright egg, broad at the chest */}
            <path data-body d="M50,36 C30,36 24,60 26,84 C28,104 38,114 50,116 C62,114 72,104 74,84 C76,60 70,36 50,36 Z" fill="#0b0b10" stroke="#2a2f3a" strokeWidth="0.8" />
            <path d="M46,44 C40,54 40,74 44,98 C44,86 44,66 50,46 Z" fill="#1a1b22" opacity="0.9" />
            {/* The wings, folded against the flanks: long tapered shapes
                from the shoulder down, each with its own edge against the
                body. The near wing sweeps down and across to cross over
                the tail; the far one shows only its outer edge. Primary
                feathers lie in a few overlapping lines toward each tip. */}
            <path data-wing d="M66,46 C76,56 78,80 72,100 C69,108 62,110 58,106 C64,90 67,68 63,50 Z" fill="#0e0f14" stroke="#2e333d" strokeWidth="0.9" />
            <path d="M70,76 L63,96 M72,86 L64,102" stroke="#262a33" strokeWidth="0.9" opacity="0.8" />
            <path data-wing d="M34,46 C24,58 23,84 32,102 C38,112 52,114 62,106 C52,100 46,82 42,52 Z" fill="#131419" stroke="#2e333d" strokeWidth="0.9" />
            <path d="M36,52 C30,64 30,84 36,100" fill="none" stroke="#2a2f3a" strokeWidth="1" opacity="0.9" />
            <path d="M34,84 L50,104 M31,92 L46,108 M38,96 L54,110" stroke="#262a33" strokeWidth="0.9" opacity="0.85" />
            <path d="M40,104 C48,110 56,110 62,106" fill="none" stroke="#3a3f4a" strokeWidth="0.8" opacity="0.7" />
            {/* Legs and feet: toes splayed on the surface at the bottom edge */}
            {[42, 58].map((x) => (
              <g key={x} data-foot>
                <path d={`M${x},112 L${x},124`} stroke="#3a3a40" strokeWidth="2.6" strokeLinecap="round" />
                <path d={`M${x},124 L${x - 8},129 M${x},124 L${x + 1},130 M${x},124 L${x + 8},128.5`} stroke="#3a3a40" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              </g>
            ))}
            {/* The head, turned toward the room, on its own pivot */}
            <g data-raven-head className="hh-raven-head">
              <path d="M46,16 C36,16 30,24 31,33 C32,42 40,46 48,45 C56,44 61,38 60,30 C59,22 54,16 46,16 Z" fill="#0b0b10" stroke="#2a2f3a" strokeWidth="0.8" />
              <path data-beak d="M38,30 L18,44 L40,40 Z" fill="#1c1c22" stroke="#33343c" strokeWidth="0.6" />
              <path d="M38,30 L18,44" stroke="#4a4b55" strokeWidth="0.9" />
              <circle data-eye cx="38" cy="27" r="2.6" fill="#050508" />
              <circle cx="37.1" cy="26.1" r="0.9" fill="#d9d4c8" />
              <circle data-eye cx="53" cy="26" r="1.9" fill="#050508" />
              <circle cx="52.4" cy="25.3" r="0.6" fill="#d9d4c8" opacity="0.8" />
            </g>
          </svg>
        </div>
      )}

      {/* The armchair: a wingback drawn up beside the fire, facing it. */}
      {armchairConfig.enabled && (
        <div
          data-armchair
          className="hh-armchair pointer-events-none"
          style={{
            position: 'fixed',
            bottom: 0,
            left: `${(armchairConfig.left ?? 0.27) * 100}%`,
            zIndex: -17
          }}
        >
          <svg viewBox="0 0 200 200" width="100%" style={{ display: 'block', overflow: 'visible' }}>
            <defs>
              {/* The fire is to the left; its light lands on that side. */}
              <linearGradient id="hh-chair-light" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor="#ffb060" stopOpacity="0.22" />
                <stop offset="0.55" stopColor="#ffb060" stopOpacity="0" />
              </linearGradient>
              {/* ...and only on the chair. A wash over the chair's whole box
                  showed as a brown rectangle on the wall behind it. */}
              <clipPath id="hh-chair-shape">
                <path d="M32,70 Q10,72 8,104 L12,138 L36,138 L36,70 Z" />
                <path d="M168,70 Q190,72 192,104 L188,138 L164,138 L164,70 Z" />
                <path d="M34,138 L34,60 Q34,26 70,24 L130,24 Q166,26 166,60 L166,138 Z" />
                <rect x="10" y="118" width="52" height="46" rx="20" />
                <rect x="138" y="118" width="52" height="46" rx="20" />
                <rect x="56" y="128" width="88" height="34" rx="10" />
                <rect x="14" y="160" width="172" height="20" rx="4" />
              </clipPath>
            </defs>
            {/* Shadow on the boards */}
            <ellipse cx="100" cy="194" rx="92" ry="6" fill="#000" opacity="0.45" />
            {/* Legs: turned wood, four of them, the back pair peeking out */}
            {[22, 56, 132, 166].map((lx, i) => (
              <g key={lx} data-leg>
                <rect x={lx} y={i === 1 || i === 2 ? 172 : 174} width="12" height="18" rx="3" fill="#2b1d12" />
                <rect x={lx - 1} y="186" width="14" height="6" rx="3" fill="#1a110a" />
              </g>
            ))}
            {/* The back, tall, with its wings */}
            <path data-wing d="M32,70 Q10,72 8,104 L12,138 L36,138 L36,70 Z" fill="#642528" />
            <path data-wing d="M168,70 Q190,72 192,104 L188,138 L164,138 L164,70 Z" fill="#521d20" />
            <path d="M34,138 L34,60 Q34,26 70,24 L130,24 Q166,26 166,60 L166,138 Z" fill="#5a1f22" />
            {/* The inner back: recessed, tufted */}
            <rect x="46" y="38" width="108" height="96" rx="12" fill="#4a181b" />
            {[[72, 62], [100, 56], [128, 62], [86, 88], [114, 88], [72, 114], [100, 118], [128, 114]].map(([cx, cy]) => (
              <g key={`${cx}-${cy}`}>
                <circle cx={cx} cy={cy} r="3.2" fill="#2e0e10" />
                <circle cx={cx - 1} cy={cy - 1} r="1.2" fill="#7a3538" />
              </g>
            ))}
            {/* Piping along the back and wings - drawn BEFORE the arms, so
                where the arms cover the back they cover the piping too. */}
            <path data-piping d="M34,138 L34,60 Q34,26 70,24 L130,24 Q166,26 166,60 L166,138" fill="none" stroke="#8a3d40" strokeWidth="1.8" />
            <path data-piping d="M32,70 Q10,72 8,104 L12,138" fill="none" stroke="#8a3d40" strokeWidth="1.5" />
            <path data-piping d="M168,70 Q190,72 192,104 L188,138" fill="none" stroke="#6e3033" strokeWidth="1.5" />
            {/* The arms: rolled, with a highlight along the roll */}
            <rect data-arm x="10" y="118" width="52" height="46" rx="20" fill="#6e2b2e" />
            <rect data-arm x="138" y="118" width="52" height="46" rx="20" fill="#5e2326" />
            <ellipse cx="36" cy="128" rx="18" ry="6" fill="#8a393d" opacity="0.6" />
            <ellipse cx="164" cy="128" rx="18" ry="6" fill="#743033" opacity="0.5" />
            {/* The seat cushion, plump, between the arms */}
            <rect data-cushion x="56" y="128" width="88" height="34" rx="10" fill="#742f32" />
            <path d="M58,146 Q100,152 142,146" fill="none" stroke="#4a181b" strokeWidth="2" opacity="0.7" />
            {/* The skirt */}
            <rect data-skirt x="14" y="160" width="172" height="20" rx="4" fill="#4e1a1d" />
            <path d="M16,162 L184,162" stroke="#7a3538" strokeWidth="1.5" opacity="0.6" />
            {/* Firelight on the near side, clipped to the chair itself */}
            <rect data-chair-light x="8" y="24" width="184" height="156" fill="url(#hh-chair-light)" clipPath="url(#hh-chair-shape)" />
          </svg>
        </div>
      )}

      {/* Mist Effect - Behind movie cards, creates translucent effect */}
      {/* Video Background for Tomb Theme */}
      {theme.background?.videoBackground && (
        <video
          className="fixed inset-0 w-full h-full object-cover pointer-events-none"
          style={{
            zIndex: -25,
            minWidth: '100%',
            minHeight: '100%',
            width: '100vw',
            height: '100vh',
            top: 0,
            left: 0
          }}
          data-tomb-background
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
        >
          {/* Responsive video sources */}
          {typeof theme.background.videoBackground === 'object' ? (
            <>
              <source
                src={theme.background.videoBackground.desktop}
                type="video/mp4"
                media="(min-width: 1024px)"
              />
              <source
                src={theme.background.videoBackground.mobile}
                type="video/mp4"
                media="(max-width: 1023px)"
              />
              {/* Fallback for older browsers */}
              <source src={theme.background.videoBackground.desktop} type="video/mp4" />
            </>
          ) : (
            <source src={theme.background.videoBackground} type="video/mp4" />
          )}
        </video>
      )}

      {theme.background?.mistEffect && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: -20 }}>
          {theme.background?.tombWalking ? (
            // Tomb Walking Atmosphere - Shadowy, mysterious effects
            <>
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  background: `
                    linear-gradient(45deg, transparent 0%, rgba(26, 26, 26, 0.4) 25%, transparent 50%, rgba(13, 13, 13, 0.3) 75%, transparent 100%),
                    linear-gradient(135deg, rgba(45, 45, 45, 0.3) 0%, transparent 30%, rgba(26, 26, 26, 0.4) 60%, transparent 100%)
                  `,
                  animation: 'tombWalk1 12s ease-in-out infinite'
                }}
              />

              <div
                className="absolute inset-0 opacity-15"
                style={{
                  background: `
                    linear-gradient(90deg, rgba(26, 26, 26, 0.3) 0%, transparent 40%, rgba(45, 45, 45, 0.4) 70%, transparent 100%),
                    radial-gradient(ellipse at 70% 30%, rgba(13, 13, 13, 0.4) 0%, transparent 60%)
                  `,
                  animation: 'tombWalk2 16s ease-in-out infinite reverse'
                }}
              />

              <div
                className="absolute inset-0 opacity-10"
                style={{
                  background: `
                    linear-gradient(180deg, rgba(51, 51, 51, 0.2) 0%, transparent 50%, rgba(38, 38, 38, 0.3) 100%),
                    linear-gradient(270deg, transparent 0%, rgba(26, 26, 26, 0.3) 50%, transparent 100%)
                  `,
                  animation: 'tombFlicker 8s ease-in-out infinite'
                }}
              />
            </>
          ) : theme.background?.forestMist ? (
            // Dark Forest Mist - Drifting fog patterns
            <>
              {/* Horizontal drifting fog banks */}
              <div
                className="absolute inset-0 opacity-30"
                style={{
                  background: `
                    linear-gradient(90deg, transparent 0%, rgba(40, 80, 40, 0.6) 20%, rgba(30, 60, 30, 0.4) 40%, transparent 60%),
                    linear-gradient(270deg, transparent 0%, rgba(50, 50, 50, 0.5) 30%, rgba(35, 70, 35, 0.3) 70%, transparent 100%)
                  `,
                  transform: 'translateX(0%)',
                  animation: 'forestDrift1 40s linear infinite'
                }}
              />

              {/* Vertical rolling fog */}
              <div
                className="absolute inset-0 opacity-25"
                style={{
                  background: `
                    linear-gradient(180deg, transparent 0%, rgba(45, 75, 45, 0.4) 25%, rgba(40, 40, 40, 0.3) 50%, rgba(35, 70, 35, 0.4) 75%, transparent 100%),
                    radial-gradient(ellipse at 70% 30%, rgba(55, 55, 55, 0.3) 0%, transparent 70%)
                  `,
                  transform: 'translateY(0%)',
                  animation: 'forestDrift2 50s linear infinite reverse'
                }}
              />

              {/* Diagonal wisps */}
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  background: `
                    linear-gradient(135deg, transparent 0%, rgba(30, 60, 30, 0.4) 30%, transparent 60%, rgba(45, 45, 45, 0.3) 80%, transparent 100%),
                    linear-gradient(45deg, rgba(40, 80, 40, 0.2) 0%, transparent 40%, rgba(50, 50, 50, 0.3) 70%, transparent 100%)
                  `,
                  animation: 'forestDrift3 35s linear infinite'
                }}
              />

            </>
          ) : (
            // Original Horror Classic Mist
            <>
              <div
                className="absolute inset-0 opacity-20"
                style={{
                  background: `
                    radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
                    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.15) 0%, transparent 50%),
                    radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%)
                  `,
                  animation: 'mistSwirl1 20s ease-in-out infinite alternate'
                }}
              />
              <div
                className="absolute inset-0 opacity-15"
                style={{
                  background: `
                    radial-gradient(circle at 60% 60%, rgba(255, 255, 255, 0.1) 0%, transparent 60%),
                    radial-gradient(circle at 90% 10%, rgba(120, 119, 198, 0.25) 0%, transparent 50%),
                    radial-gradient(circle at 10% 90%, rgba(255, 255, 255, 0.08) 0%, transparent 50%)
                  `,
                  animation: 'mistSwirl2 25s ease-in-out infinite alternate-reverse'
                }}
              />
              <div
                className="absolute inset-0 opacity-10"
                style={{
                  background: `
                    radial-gradient(circle at 70% 30%, rgba(120, 119, 198, 0.2) 0%, transparent 70%),
                    radial-gradient(circle at 30% 70%, rgba(255, 255, 255, 0.12) 0%, transparent 60%)
                  `,
                  animation: 'mistSwirl3 30s ease-in-out infinite alternate'
                }}
              />
            </>
          )}
        </div>
      )}

      {/* Animated Creatures - Above movie cards for visibility */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 5 }}>
        {/* Render bats - position is driven by the rAF loop via transform */}
        {batConfig.enabled && bats.map(bat => (
          <img
            key={bat.id}
            ref={(el) => {
              if (el) batNodes.current.set(bat.id, el);
              else batNodes.current.delete(bat.id);
            }}
            src={batConfig.image || '/Bat.webp'}
            alt="bat"
            data-creature
            className="w-16 h-16 object-contain"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              willChange: 'transform',
              transform: `translate3d(${bat.x}px, ${bat.y}px, 0) scale(${bat.size})${bat.direction === 1 ? ' scaleX(-1)' : ''}`,
              filter: batConfig.filter || 'drop-shadow(0 0 4px rgba(0,0,0,0.5)) brightness(0.7)',
              opacity: batConfig.opacity || 0.8,
              pointerEvents: 'none',
              zIndex: 6
            }}
          />
        ))}

        {/* Render Mothman */}
        {mothmanConfig.enabled && mothman && (
          <img
            key={mothman.id}
            ref={(el) => { mothmanNode.current = el; }}
            src={mothmanConfig.image || '/Mothman.webp'}
            alt="mothman"
            data-creature
            className="w-16 h-16 object-contain"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              willChange: 'transform',
              transform: `translate3d(${mothman.x}px, ${mothman.y}px, 0) scale(${mothmanConfig.scale || 4.0})${mothman.direction === 1 ? ' scaleX(-1)' : ''}`,
              filter: mothmanConfig.filter || 'drop-shadow(0 0 6px rgba(255,0,0,0.4)) brightness(0.8) contrast(1.1)',
              opacity: mothmanConfig.opacity || 0.7,
              pointerEvents: 'none',
              zIndex: 7
            }}
          />
        )}

        {/* Render Glowing Eyes - for Dark Forest theme */}
        {/* Opacity and glow filter now live on the wrapper and are written by the
            rAF loop; the two eye divs keep their static colour, scale and glow. */}
        {eyesConfig.enabled && eyes.map(eye => {
          const glowPulse = Math.sin(eye.blinkPhase) * 0.3 + 0.7;
          const currentOpacity = eye.isBlinking ? 0.1 : eye.opacity * glowPulse;

          return (
            <div
              key={eye.id}
              ref={(el) => {
                if (el) eyeNodes.current.set(eye.id, el);
                else eyeNodes.current.delete(eye.id);
              }}
              data-creature
              className="absolute pointer-events-none"
              style={{
                left: 0,
                top: 0,
                willChange: 'transform, opacity',
                transform: `translate3d(${eye.x}px, ${eye.y}px, 0)`,
                opacity: currentOpacity,
                filter: `brightness(${glowPulse * 1.2})`,
                zIndex: 6
              }}
            >
              {/* Eye pair - left and right eye */}
              <div className="flex items-center gap-2">
                {/* Left Eye */}
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: eye.color,
                    transform: `scale(${eye.size})`,
                    boxShadow: `0 0 ${eye.glowIntensity}px ${eye.color}, 0 0 ${eye.glowIntensity * 2}px ${eye.color}`
                  }}
                />
                {/* Right Eye */}
                <div
                  className="w-3 h-3 rounded-full"
                  style={{
                    backgroundColor: eye.color,
                    transform: `scale(${eye.size})`,
                    boxShadow: `0 0 ${eye.glowIntensity}px ${eye.color}, 0 0 ${eye.glowIntensity * 2}px ${eye.color}`
                  }}
                />
              </div>
            </div>
          );
        })}

        {/* Render Tomb Shadows - for Ancient Tomb theme */}
        {tombShadowConfig.enabled && tombShadows.map(shadow => {
          // Create different shadow shapes for variety. Ids are integers,
          // so this is a plain modulo; the old `floor(id * 3) % 3` relied on
          // the fractional ids and would always give 0 now.
          const shadowType = shadow.id % 3;

          return (
            <div
              key={shadow.id}
              ref={(el) => {
                if (el) shadowNodes.current.set(shadow.id, el);
                else shadowNodes.current.delete(shadow.id);
              }}
              data-creature
              className="absolute pointer-events-none"
              style={{
                left: 0,
                top: 0,
                willChange: 'transform',
                transform: `translate3d(${shadow.x}px, ${shadow.y}px, 0)`,
                zIndex: 2, // Behind other creatures but above background
              }}
            >
              <svg
                width={shadow.size}
                height={shadow.size * 0.6}
                viewBox="0 0 100 60"
                data-shape={shadowType}
                style={{
                  opacity: shadow.opacity,
                  filter: 'blur(2px) contrast(0.8)',
                  animation: `tombShadow ${8 + (shadow.id % 4)}s ease-in-out infinite`
                }}
              >
                {shadowType === 0 && (
                  // Human-like walking shadow
                  <path
                    d="M20,50 Q25,45 30,50 Q35,40 40,50 Q45,35 50,50 Q55,40 60,50 Q65,45 70,50 Q75,50 80,50"
                    fill="none"
                    stroke={shadow.color}
                    strokeWidth="8"
                    strokeLinecap="round"
                    opacity="0.6"
                  />
                )}
                {shadowType === 1 && (
                  // Elongated mysterious shadow
                  <ellipse
                    ref={(el) => {
                      if (el) shadowEllipseNodes.current.set(shadow.id, el);
                      else shadowEllipseNodes.current.delete(shadow.id);
                    }}
                    cx="50"
                    cy="40"
                    rx="35"
                    ry="15"
                    fill={shadow.color}
                    opacity="0.4"
                    transform={`rotate(${(shadow.walkPhase * 10) % 360} 50 40)`}
                  />
                )}
                {shadowType === 2 && (
                  // Irregular, ghostly shadow
                  <path
                    d="M15,45 Q30,35 45,45 Q60,30 75,45 Q85,50 75,55 Q60,50 45,55 Q30,50 15,55 Z"
                    fill={shadow.color}
                    opacity="0.3"
                  />
                )}
              </svg>
            </div>
          );
        })}

        {/* Render Sarcophagus - for Ancient Tomb theme */}
        {/* Opacity is driven by the tombFlicker keyframes, so the loop only
            writes transform here. */}
        {sarcophagusConfig.enabled && sarcophagus && (
          <img
            key={sarcophagus.id}
            ref={(el) => { sarcophagusNode.current = el; }}
            src={sarcophagusConfig.image || '/Sarcophagus.webp'}
            alt="sarcophagus"
            data-creature
            className="w-32 h-32 object-contain"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              willChange: 'transform',
              transform: `translate3d(${sarcophagus.x}px, ${sarcophagus.y}px, 0) scale(${sarcophagus.scale})${sarcophagus.direction < 0 ? ' scaleX(-1)' : ''}`,
              filter: sarcophagus.filter,
              opacity: sarcophagus.opacity,
              pointerEvents: 'none',
              zIndex: 4,
              animation: `tombFlicker ${6 + (sarcophagus.id % 3)}s ease-in-out infinite`
            }}
          />
        )}

        {/* The walker. An animated GIF the browser plays on its own; the loop
            writes the transform and, on a turn, swaps the file. A facing with
            no file of its own shows its mirror image. Mounted at the spawn
            position for the same reason as the chase. */}
        {walkerConfig.enabled && walker && (() => {
          const sprite = walkerSprite(walkerConfig.images, walker.facing);
          return (
            <img
              key={`walker-${walker.id}`}
              ref={(el) => { walkerNode.current = el; }}
              src={sprite.src}
              alt="walker"
              data-walker
              data-facing={walker.facing}
              data-creature
              className="w-32 h-32 object-contain"
              style={{
                position: 'absolute',
                left: 0,
                top: 0,
                willChange: 'transform',
                transform: `translate3d(${walker.startX}px, ${walker.baseY}px, 0) scale(${walker.scale})${sprite.flip ? ' scaleX(-1)' : ''}`,
                transformOrigin: 'bottom center',
                pointerEvents: 'none',
                zIndex: 5,
                opacity: walkerConfig.opacity ?? 0.95,
                filter: 'drop-shadow(0 0 10px rgba(0,0,0,0.7))'
              }}
            />
          );
        })()}

        {/* The ghosts. Pale sheeted figures, drawn here rather than loaded;
            the loop writes transform and opacity. Mounted at the spawn
            position and invisible, so the fade-up starts off-screen. */}
        {ghostConfig.enabled && ghosts.map((g) => (
          <div
            key={`ghost-${g.id}`}
            ref={(el) => {
              if (el) ghostNodes.current.set(g.id, el);
              else ghostNodes.current.delete(g.id);
            }}
            data-ghost
            data-creature
            className="w-28 h-32"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              willChange: 'transform, opacity',
              transform: `translate3d(${g.startX}px, ${g.baseY}px, 0) scale(${g.scale})${g.direction === 1 ? ' scaleX(-1)' : ''}`,
              opacity: 0,
              pointerEvents: 'none',
              zIndex: 5,
              // A cold, dim glow, and the faintest blur so the edge never
              // reads as cut paper. Bluish on purpose: the only other light
              // in the room is a candle, and these are not warm.
              filter: 'drop-shadow(0 0 10px rgba(160, 185, 230, 0.4)) blur(0.5px)'
            }}
          >
            <svg viewBox="0 0 100 140" width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
              {/* The sheet: narrow, drawn long, the hem torn to uneven tatters
                  rather than folded. Grey-white, not white. Facing left. */}
              <path
                data-sheet
                d="M50,4 C36,4 26,18 26,44 L24,96 L20,102 L27,124 L31,104 L36,136 L41,108 L46,130 L51,110 L57,140 L62,112 L67,128 L72,106 L77,122 L80,98 L76,44 C76,18 64,4 50,4 Z"
                fill="#d6dbe6"
                opacity="0.84"
              />
              {/* Something narrower inside the sheet, where it hangs on a form */}
              <path d="M50,14 C42,14 38,30 38,52 L36,100 L50,118 L64,100 L62,52 C62,30 58,14 50,14 Z" fill="#8e98ad" opacity="0.28" />
              {/* Two arms, both too long. One reaches ahead; the other hangs
                  behind and a little lower, fingers trailing. */}
              <path data-hand d="M27,58 C18,62 10,70 6,80" fill="none" stroke="#d6dbe6" strokeWidth="6" strokeLinecap="round" opacity="0.8" />
              {[[6, 80, -2, 94], [6, 80, 3, 96], [6, 80, 9, 93], [6, 80, 12, 88]].map(([x1, y1, x2, y2], i) => (
                <path key={`f-${i}`} data-finger d={`M${x1},${y1} L${x2},${y2}`} stroke="#d6dbe6" strokeWidth="1.6" strokeLinecap="round" opacity="0.75" />
              ))}
              <path data-hand d="M74,64 C84,70 90,82 92,96" fill="none" stroke="#d6dbe6" strokeWidth="5.5" strokeLinecap="round" opacity="0.72" />
              {[[92, 96, 86, 110], [92, 96, 91, 113], [92, 96, 97, 110], [92, 96, 101, 104]].map(([x1, y1, x2, y2], i) => (
                <path key={`b-${i}`} data-finger d={`M${x1},${y1} L${x2},${y2}`} stroke="#d6dbe6" strokeWidth="1.5" strokeLinecap="round" opacity="0.68" />
              ))}
              {/* Eye sockets: hollow, uneven, nothing in them */}
              <ellipse data-eye cx="40" cy="42" rx="6.5" ry="10" fill="#04050a" opacity="0.92" transform="rotate(-12 40 42)" />
              <ellipse data-eye cx="60" cy="40" rx="5.5" ry="12" fill="#04050a" opacity="0.92" transform="rotate(14 60 40)" />
              {/* And a mouth drawn down into a long wail */}
              <path data-mouth d="M44,58 Q50,54 56,58 L58,82 Q50,94 42,82 Z" fill="#04050a" opacity="0.86" />
            </svg>
          </div>
        ))}

        {/* The cloaked man. Fixed to the floor; the loop writes only x and a
            slight tread, the cloak's sway and the flames are CSS. Drawn
            facing left; mirrored for the other way. Twice the fireplace. */}
        {cloakedConfig.enabled && cloaked && (
          <div
            key={`cloaked-${cloaked.id}`}
            ref={(el) => { cloakedNode.current = el; }}
            data-cloaked
            data-stature={cloakedConfig.stature ?? 2}
            data-creature
            className="hh-cloaked"
            style={{
              position: 'fixed',
              left: 0,
              bottom: 0,
              height: fireplaceHeights(cloakedConfig.stature ?? 2),
              aspectRatio: '1 / 3',
              willChange: 'transform',
              transform: `translate3d(${cloaked.startX}px, 0px, 0)${cloaked.direction === 1 ? ' scaleX(-1)' : ''}`,
              transformOrigin: 'bottom center',
              pointerEvents: 'none',
              zIndex: 4,
              opacity: cloakedConfig.opacity ?? 0.96
            }}
          >
            {/* The candelabra's light, carried with him */}
            <div className="hh-candle-glow hh-cloaked-glow" />
            <svg viewBox="0 0 100 300" width="100%" height="100%" style={{ display: 'block', overflow: 'visible' }}>
              <g className="hh-cloaked-body">
                {/* The cloak, hood to hem, with a few folds caught by the light */}
                <path
                  data-cloak
                  d="M60,18 C42,24 38,46 36,70 C32,120 26,200 24,296 L96,296 C94,200 88,120 84,70 C82,46 78,24 60,18 Z"
                  fill="#0c0a10"
                />
                <path d="M60,18 C42,24 38,46 36,70 C32,120 26,200 24,296" fill="none" stroke="#28222f" strokeWidth="1.5" />
                <path d="M58,90 C54,150 50,220 48,296" fill="none" stroke="#1c1824" strokeWidth="1.2" />
                <path d="M70,110 C70,170 72,230 74,296" fill="none" stroke="#1c1824" strokeWidth="1.2" />
                {/* The hood, and nothing to see inside it */}
                <path d="M60,10 C46,10 38,24 40,44 C48,38 72,38 80,44 C82,24 74,10 60,10 Z" fill="#100d15" />
                <ellipse cx="60" cy="36" rx="12" ry="9" fill="#050408" />
                {/* The arm held out, and the hand that catches the light */}
                <path d="M46,92 Q30,108 20,128" fill="none" stroke="#0c0a10" strokeWidth="11" strokeLinecap="round" />
                <circle cx="19" cy="130" r="5.5" fill="#cbb79a" />
                {/* The candelabra: stem, two arms, three candles */}
                <rect x="17.5" y="104" width="3" height="26" fill="#8a7a3a" />
                <path d="M19,108 Q19,96 6,92 M19,108 Q19,96 32,92" fill="none" stroke="#8a7a3a" strokeWidth="3" strokeLinecap="round" />
                {[6, 19, 32].map((cx, i) => (
                  <g key={cx}>
                    <rect x={cx - 2.5} y={i === 1 ? 86 : 78} width="5" height={i === 1 ? 14 : 14} fill="#f1e6c8" />
                    <g data-flame className="hh-flame" style={{ animationDuration: `${0.5 + i * 0.09}s`, animationDelay: `${-i * 0.2}s` }}>
                      <path
                        d={`M${cx},${i === 1 ? 86 : 78} C${cx - 4},${i === 1 ? 80 : 72} ${cx - 3},${i === 1 ? 72 : 64} ${cx},${i === 1 ? 66 : 58} C${cx + 3},${i === 1 ? 72 : 64} ${cx + 4},${i === 1 ? 80 : 72} ${cx},${i === 1 ? 86 : 78} Z`}
                        fill="#ffb400"
                        opacity="0.95"
                      />
                    </g>
                  </g>
                ))}
              </g>
            </svg>
          </div>
        )}

        {/* The jump scare: the mask, filling the frame, for under half a
            second. The viewBox is cropped to the head so the mask fills the
            box; CSS scales that to most of the viewport. Fixed and above the
            interface, pointer-events none so it can never catch a click, and
            aria-hidden because it is decoration, however rude. */}
        {/* Rendered through a portal as the LAST child of document.body, so
            its layer depends on nothing in the app tree: no ancestor stacking
            context, overflow clip or containing-block trap can put a calendar
            block in front of it. Inside the tree it measured z-index 70 in
            the root context and still read as behind the grid on a signed-in
            desktop; this removes the tree from the question entirely. */}
        {jumpScareConfig.enabled && scare && typeof document !== 'undefined' && createPortal(
          <div
            key={`scare-${scare.id}`}
            data-jumpscare
            className="sw-jumpscare"
            aria-hidden="true"
            style={{ '--sw-tilt': `${scare.tilt}deg`, '--sw-shift': `${scare.offsetX}vw` }}
          >
            <svg viewBox="40 1 20 22" className="sw-jumpscare-face" style={{ display: 'block' }}>
              {/* The viewBox is 20 units wide and fills most of the viewport,
                  so 0.22 units is roughly a 10px rim. */}
              <path data-mask d={MASK.mask} fill={MASK.fill} stroke={MASK.rim} strokeWidth="0.22" />
              {MASK.eyes.map((d, i) => (
                <path key={i} data-eye d={d} fill={MASK.eyeFill} />
              ))}
              {MASK.vents.map(([cx, cy], i) => (
                <circle key={`v-${i}`} data-vent cx={cx} cy={cy} r={MASK.ventR} fill={MASK.ventFill} />
              ))}
            </svg>
          </div>,
          document.body
        )}

        {/* The chase. Position is written by the rAF loop; the two <g> gait
            frames per figure are flipped there too. A real image replaces the
            drawn silhouette when the theme supplies one in chase.images. */}
        {chaseConfig.enabled && chase && [
          {
            key: 'teen', frames: TEEN_FRAMES, node: teenNode, frameNodes: teenFrameNodes,
            image: chaseConfig.images?.teen, opacity: chaseConfig.opacity?.teen ?? 0.85,
            // Ink: dark slate, NOT near-black. The chase runs through the foot
            // of the gradient, which is #02-#06 per channel; a #05080a figure
            // darkened by a brightness filter measured 1.0:1 against it -
            // literally invisible, with only the glint and the eyes drifting
            // past. These sit where the UI panels sit, which are plainly
            // visible on the same gradient. The killer is the darker shape.
            ink: '#243040', zIndex: 6, trailBy: 0, stature: chase.teenStature
          },
          {
            key: 'slasher', frames: SLASHER_FRAMES, node: slasherNode, frameNodes: slasherFrameNodes,
            image: chaseConfig.images?.slasher, opacity: chaseConfig.opacity?.slasher ?? 0.95,
            ink: '#1a2230', zIndex: 5, trailBy: chase.gapStart, stature: chase.slasherStature
          }
        ].map((f) => (
          <div
            key={`chase-${chase.id}-${f.key}`}
            ref={(el) => { f.node.current = el; }}
            data-chase={f.key}
            data-creature
            className="w-24 h-24"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              willChange: 'transform',
              // Mounted at the spawn position. Without this the pair sits at
              // (0,0) - the top-left corner - for the one frame between React
              // rendering it and the loop's first write.
              transform: `translate3d(${chase.startX - chase.direction * f.trailBy}px, ${chase.baseY}px, 0)`
                + ` scale(${chase.scale * f.stature})${chase.direction === 1 ? ' scaleX(-1)' : ''}`,
              // Both figures scale from their feet, so the taller one grows
              // UPWARD from the same ground instead of sinking below it.
              transformOrigin: 'bottom center',
              pointerEvents: 'none',
              zIndex: f.zIndex,
              opacity: f.opacity,
              // A pale cold-steel rim so the edge reads on bare black, then a
              // soft dark halo for depth when the pair crosses a lit panel.
              // No brightness(): that was what pushed the figures to black.
              filter: 'drop-shadow(0 0 2px rgba(159,179,200,0.45)) drop-shadow(0 0 8px rgba(0,0,0,0.6))'
            }}
          >
            {f.image ? (
              <img src={f.image} alt={f.key} className="w-full h-full object-contain" />
            ) : (
              <svg viewBox="0 0 100 100" width="100%" height="100%" style={{ display: 'block' }}>
                {f.frames.map((frame, i) => (
                  <g
                    key={i}
                    ref={(el) => { f.frameNodes.current[i] = el; }}
                    style={{ display: i === 0 ? '' : 'none' }}
                    fill={f.ink}
                    stroke={f.ink}
                    strokeWidth="9"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {/* A plain head, or a mask with eyes catching light. */}
                    {frame.head && (
                      <circle cx={frame.head[0]} cy={frame.head[1]} r={frame.head[2]} stroke="none" />
                    )}
                    {/* The hockey mask carries its own colours - white shell,
                        red eyes, dark vents - with a thin dark rim so it keeps
                        its edge against the figure's ink. */}
                    {frame.mask && (
                      <path data-mask d={frame.mask} fill={frame.fill} stroke={frame.rim} strokeWidth="1.2" />
                    )}
                    {frame.eyes && frame.eyes.map((d, j) => (
                      <path key={`eye-${j}`} data-eye d={d} fill={frame.eyeFill} stroke="none" />
                    ))}
                    {frame.vents && frame.vents.map(([cx, cy], j) => (
                      <circle key={`vent-${j}`} data-vent cx={cx} cy={cy} r={frame.ventR} fill={frame.ventFill} stroke="none" />
                    ))}
                    {frame.paths.map((d, j) => <path key={j} d={d} fill="none" />)}
                    {/* The machete: a filled shape, not a limb stroke, with a
                        guard and a lit spine so it reads against the figure. */}
                    {frame.blade && <path data-blade d={frame.blade} fill={f.ink} stroke="none" />}
                    {frame.guard && <path d={frame.guard} fill="none" strokeWidth="5" />}
                    {frame.glint && (
                      <path data-glint d={frame.glint} fill="none" stroke="#9fb3c8" strokeWidth="1.6" strokeOpacity="0.8" />
                    )}
                  </g>
                ))}
              </svg>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

export default AnimatedBackground;
